import React, { useState, useEffect, useRef } from 'react';
import {
    Modal, Select, Button, Spin, Alert, Tag, Radio,
    Row, Col, Progress, Divider, message
} from 'antd';
import {
    FilePdfOutlined, PrinterOutlined, FileExcelOutlined, StarFilled,
    LoadingOutlined
} from '@ant-design/icons';

import NarrativeReportDocument from './NarrativeReportDocument';
import QoCService from '../../services/QoCService';
import { exportFacilityAssessmentExcel } from '../../utils/qocExcelExport';
import FacilityService from '../../services/FacilityService';
import moment from 'moment';
import { trailReportGenerate, trailReportExport, TRAIL_MODULES } from '../../utils/activityTrail';
const { Option } = Select;

const CLAUSES = [
    { code:'AACC', title:'Access, Assessment and Continuity of Care',           color:'#0ea5e9', num:1 },
    { code:'COP',  title:'Care of the Patient',                                  color:'#8b5cf6', num:2 },
    { code:'MOM',  title:'Management of Medication',                             color:'#f59e0b', num:3 },
    { code:'PRPE', title:"Patient Rights and Patient Education",                  color:'#10b981', num:4 },
    { code:'IPCM', title:'Infection Prevention and Control Management',           color:'#ef4444', num:5 },
    { code:'PSQI', title:'Patient Safety and Quality Improvement',               color:'#3b82f6', num:6 },
    { code:'GROM', title:'Governance, Roles and Responsibilities of Management', color:'#6366f1', num:7 },
    { code:'FMS',  title:'Facility Management, Safety and Support Services',     color:'#ec4899', num:8 },
    { code:'HRE',  title:'Human Resource and Education',                         color:'#14b8a6', num:9 },
    { code:'ISM',  title:'Information System Management',                        color:'#a855f7', num:10 },
];

const RATING = [
    { min:98,  max:100,  label:'Outstanding',          stars:5, color:'#389e0d', bg:'#e6f7ff', iso:'Ready for ISO accreditation' },
    { min:95,  max:97.9, label:'Excellent',            stars:4, color:'#1890ff', bg:'#e6f7ff', iso:'Ready for ISO accreditation' },
    { min:90,  max:94.9, label:'Good',                 stars:3, color:'#52c41a', bg:'#f6ffed', iso:'Almost ready for ISO accreditation' },
    { min:85,  max:89.9, label:'Fair',                 stars:2, color:'#FFFF00', bg:'#feffe6', iso:'Somewhat ready for ISO accreditation' },
    { min:70,  max:84.9, label:'Requires Improvement', stars:1, color:'#fa8c16', bg:'#fff7e6', iso:'Not ready for ISO accreditation' },
    { min:0,   max:69.9, label:'Inadequate',           stars:0, color:'#f5222d', bg:'#fff2f0', iso:'Cause for concern' },
];
const getRating = s => RATING.find(r => s >= r.min && s <= r.max) || RATING[RATING.length-1];

// Official colour mapping matching the template document
const RATING_ROW_COLORS = {
    'Outstanding':          { bg:'#1565C0', text:'#fff' },   // dark blue
    'Excellent':            { bg:'#2E7D32', text:'#fff' },   // dark green
    'Good':                 { bg:'#558B2F', text:'#fff' },   // olive green
    'Fair':                 { bg:'#FFFF00', text:'#000' },   // yellow ← fix
    'Requires Improvement': { bg:'#E65100', text:'#fff' },   // deep orange
    'Inadequate':           { bg:'#B71C1C', text:'#fff' },   // dark red
};

const FULFILLMENT_STYLE = {
    'Met':            { color:'#389e0d', bg:'#f6ffed', icon:'✓',  short:'M'  },
    'Partially Met':  { color:'#fa8c16', bg:'#fff7e6', icon:'◑',  short:'PM' },
    'Not Met':        { color:'#f5222d', bg:'#fff2f0', icon:'✗',  short:'NM' },
    'Not Applicable': { color:'#595959', bg:'#f5f5f5', icon:'—',  short:'NA' },
    'Not Checked':    { color:'#722ed1', bg:'#f9f0ff', icon:'?',  short:'NC' },
};

function Stars({ count, size=13 }) {
    return <span>{[1,2,3,4,5].map(i=>(
        <StarFilled key={i} style={{color: i<=count?'#faad14':'#e0e0e0', fontSize:size}}/>
    ))}</span>;
}

// ─── The printable / capturable report document ───────────────────────────────
function ReportDocument({ facility, assessment, clauseScores, responses }) {
    // Inject print-colour CSS so browser print / Save as PDF preserves all colours
    useEffect(() => {
        const id = 'qoc-print-colour-css';
        if (document.getElementById(id)) return;
        const s = document.createElement('style');
        s.id = id;
        s.innerHTML = `@media print {
            * { -webkit-print-color-adjust:exact!important; print-color-adjust:exact!important; color-adjust:exact!important; }
            .report-page { page-break-after:always!important; }
        }`;
        document.head.appendChild(s);
    }, []);

    const overall   = parseFloat(assessment.overall_percentage || 0);
    const rating    = getRating(overall);
    const clauseMap = {};
    clauseScores.forEach(s => { clauseMap[s.clause_code] = s; });
    const meByClause = {};
    responses.forEach(r => {
        const code = r.clause_code;
        if (!meByClause[code]) meByClause[code] = [];
        meByClause[code].push(r);
    });

    // ── Per-clause narrative: mines comment / observation / criticality / good practice ──
    const buildClauseNarrative = (clauseCode, clauseColor, mes, score) => {
        if (!mes || mes.length === 0) return null;

        const met        = mes.filter(m => m.fulfillment === 'Met');
        const partial    = mes.filter(m => m.fulfillment === 'Partially Met');
        const notMet     = mes.filter(m => m.fulfillment === 'Not Met');
        const na         = mes.filter(m => m.fulfillment === 'Not Applicable');
        const highCrit   = mes.filter(m =>
            (m.criticality_of_observation || '').toLowerCase() === 'high' &&
            (m.fulfillment === 'Not Met' || m.fulfillment === 'Partially Met')
        );
        const medCrit    = mes.filter(m =>
            (m.criticality_of_observation || '').toLowerCase() === 'medium' &&
            (m.fulfillment === 'Not Met' || m.fulfillment === 'Partially Met')
        );
        const goodPracts = mes
            .filter(m => m.area_of_good_practice && m.area_of_good_practice.trim().length > 3)
            .map(m => m.area_of_good_practice.trim());
        const keyObs     = mes
            .filter(m => m.general_observation && m.general_observation.trim().length > 5 &&
                (m.fulfillment === 'Not Met' || m.fulfillment === 'Partially Met'))
            .map(m => m.general_observation.trim())
            .slice(0, 3);
        const keyComments = mes
            .filter(m => m.comment && m.comment.trim().length > 5 &&
                m.fulfillment === 'Not Met')
            .map(m => m.comment.trim())
            .slice(0, 2);

        const pct = score ? parseFloat(score.total_score || 0) : null;
        const r   = pct !== null ? getRating(pct) : null;

        let lines = [];

        // Opening performance sentence
        if (pct !== null) {
            const complianceWord = pct >= 90 ? 'strong' : pct >= 80 ? 'good' : pct >= 70 ? 'moderate' : pct >= 60 ? 'below the minimum acceptable' : 'poor';
            lines.push(`This clause achieved a score of ${pct.toFixed(1)}% (${r?.label}), indicating ${complianceWord} compliance. Of ${mes.length} measurable elements assessed: ${met.length} Met, ${partial.length} Partially Met, ${notMet.length} Not Met${na.length > 0 ? `, and ${na.length} Not Applicable` : ''}.`);
        }

        // Criticality
        if (highCrit.length > 0) {
            lines.push(`⚠ ${highCrit.length} high-criticality gap${highCrit.length > 1 ? 's were' : ' was'} identified — ${highCrit.length > 1 ? 'these require' : 'this requires'} immediate corrective action due to direct impact on patient safety or care quality.`);
        }
        if (medCrit.length > 0) {
            lines.push(`${medCrit.length} medium-criticality gap${medCrit.length > 1 ? 's' : ''} ${medCrit.length > 1 ? 'were' : 'was'} noted and should be addressed within the next improvement cycle.`);
        }

        // Key observations
        if (keyObs.length > 0) {
            lines.push(`Key observations from auditors: ${keyObs.join('; ')}.`);
        }
        if (keyComments.length > 0 && keyComments[0] !== keyObs[0]) {
            lines.push(`Auditor comments noted: ${keyComments.join('; ')}.`);
        }

        // Good practices
        if (goodPracts.length > 0) {
            lines.push(`✓ Areas of good practice identified: ${goodPracts.slice(0, 3).join('; ')} — these should be documented and sustained.`);
        }

        if (lines.length === 0) return null;
        return lines.join(' ');
    };

    const CELL = { padding:'5px 8px', borderBottom:'1px solid #e8e8e8', verticalAlign:'top', fontSize:10, lineHeight:1.5 };

    return (
        <div id="qoc-full-report" style={{ fontFamily:"'Segoe UI',Arial,sans-serif", color:'#222', background:'#fff', width:'100%' }}>

            {/* ── PAGE 1: COVER ── */}
            <div className="report-page" style={{ padding:'32px 36px', pageBreakAfter:'always' }}>
                <div style={{ borderBottom:'4px solid #1890ff', paddingBottom:16, marginBottom:20, textAlign:'center' }}>
                    <div style={{ fontSize:18, fontWeight:900, color:'#1890ff', letterSpacing:1, textTransform:'uppercase' }}>Eswatini Ministry of Health</div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#333', marginTop:4 }}>National Quality Management Programme (NQMP)</div>
                    <div style={{ fontSize:12, color:'#666', marginTop:3 }}>Eswatini Quality of Care (QoC) Standards Audit Report</div>
                </div>

                <Row gutter={24} style={{ marginBottom:20 }}>
                    <Col span={12}>
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                            {[
                                ['Facility Name', facility.business_name],
                                ['Type of Facility', facility.site_type||'N/A'],
                                ['Facility Owner', facility.facility_owner||'N/A'],
                                ['Facility Address', facility.address||'N/A'],
                                ['City', facility.city||'N/A'],
                            ].map(([k,v]) => (
                                <tr key={k}>
                                    <td style={{ ...CELL, color:'#666', width:140, fontWeight:600, background:'#f8f9fa', borderRight:'1px solid #e8e8e8' }}>{k}</td>
                                    <td style={{ ...CELL, fontWeight:700 }}>{v}</td>
                                </tr>
                            ))}
                        </table>
                    </Col>
                    <Col span={12}>
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                            {[
                                ['Audit Date', moment(assessment.assessment_date).format('DD MMMM YYYY')],
                                ['RC Number', facility.site_code||'N/A'],
                                ['Facility Code', facility.fac_code||'N/A'],
                                ['Region', facility.located_region||'N/A'],
                                ['License Number', facility.fac_code||'N/A'],
                            ].map(([k,v]) => (
                                <tr key={k}>
                                    <td style={{ ...CELL, color:'#666', width:130, fontWeight:600, background:'#f8f9fa', borderRight:'1px solid #e8e8e8' }}>{k}</td>
                                    <td style={{ ...CELL, fontWeight:700 }}>{v}</td>
                                </tr>
                            ))}
                        </table>
                    </Col>
                </Row>

                <div style={{ background:`linear-gradient(135deg,${rating.color}18,${rating.color}08)`,
                    border:`3px solid ${rating.color}60`, borderRadius:12, padding:'20px 28px',
                    marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <div>
                        <div style={{ fontSize:11, color:'#888', fontWeight:700, letterSpacing:1, marginBottom:4 }}>OVERALL AUDIT SCORE</div>
                        <div style={{ fontSize:52, fontWeight:900, color:rating.color, lineHeight:1 }}>{overall.toFixed(1)}%</div>
                        <div style={{ marginTop:8 }}><Stars count={rating.stars} size={24}/></div>
                    </div>
                    <div style={{ textAlign:'center' }}>
                        <div style={{ fontSize:22, fontWeight:900, color:rating.color, marginBottom:6 }}>{rating.label}</div>
                        <div style={{ fontSize:12, color:'#666', fontStyle:'italic' }}>{rating.iso}</div>
                        <Progress type="circle" percent={Math.min(100,overall)} width={90} strokeColor={rating.color}
                            format={()=><span style={{fontWeight:900,color:rating.color,fontSize:16}}>{overall.toFixed(0)}%</span>}
                            style={{ marginTop:10 }}/>
                    </div>
                    <div>
                        {RATING.map(r2=>(
                            <div key={r2.label} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5,
                                opacity:overall>=r2.min?1:0.3, padding:'2px 6px',
                                background:rating.label===r2.label?`${r2.color}18`:'transparent', borderRadius:4 }}>
                                <Stars count={r2.stars} size={10}/>
                                <span style={{ fontSize:11, color:r2.color, fontWeight:700, minWidth:130 }}>{r2.label}</span>
                                <span style={{ fontSize:10, color:'#aaa' }}>{r2.min}–{r2.max}%</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{ marginBottom:20 }}>
                    <div style={{ fontWeight:800, fontSize:13, color:'#1a1a2e', marginBottom:10, borderLeft:'4px solid #1890ff', paddingLeft:10 }}>SECTION SCORES</div>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                        <thead>
                            <tr style={{ background:'linear-gradient(135deg,#f0f5ff,#e6f0ff)' }}>
                                {['#','Clause / Section','A','Met','Partial','Not Met','Score','Stars','Interpretation'].map(h=>(
                                    <th key={h} style={{ padding:'8px 12px', textAlign: h==='Clause / Section'?'left':'center', borderBottom:'2px solid #d6e4ff' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {CLAUSES.map((cl,idx)=>{
                                const s = clauseMap[cl.code];
                                const pct = s ? parseFloat(s.total_score||0) : null;
                                const r = pct !== null ? getRating(pct) : null;
                                return (
                                    <tr key={cl.code} style={{ background:idx%2?'#fafafa':'#fff' }}>
                                        <td style={{ ...CELL, textAlign:'center' }}>
                                            <div style={{ width:22, height:22, borderRadius:'50%', background:cl.color, color:'#fff', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700 }}>{cl.num}</div>
                                        </td>
                                        <td style={{ ...CELL }}><span style={{ fontWeight:800, color:cl.color }}>{cl.code}</span><span style={{ color:'#444', marginLeft:6 }}>{cl.title}</span></td>
                                        <td style={{ ...CELL, textAlign:'center', color:'#666' }}>{s?.total_audited||'—'}</td>
                                        <td style={{ ...CELL, textAlign:'center', color:'#389e0d', fontWeight:700 }}>{s?.met_count||'—'}</td>
                                        <td style={{ ...CELL, textAlign:'center', color:'#fa8c16', fontWeight:700 }}>{s?.partially_met_count||'—'}</td>
                                        <td style={{ ...CELL, textAlign:'center', color:'#f5222d', fontWeight:700 }}>{s?.not_met_count||'—'}</td>
                                        <td style={{ ...CELL, textAlign:'center' }}>{pct!==null?<span style={{ fontWeight:900, fontSize:14, color:r.color }}>{pct.toFixed(1)}%</span>:<span style={{ color:'#ccc' }}>—</span>}</td>
                                        <td style={{ ...CELL, textAlign:'center' }}>{r?<Stars count={r.stars} size={11}/>:<span style={{ color:'#ccc' }}>—</span>}</td>
                                        <td style={{ ...CELL }}>{r?<span style={{ color:r.color, fontWeight:700 }}>{r.label}</span>:<span style={{ color:'#ccc' }}>Not assessed</span>}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* ── Facility-level narrative summary ── */}
                {(() => {
                    const assessed = clauseScores.filter(s => parseFloat(s.total_score||0) > 0);
                    if (!assessed.length) return null;

                    const scores    = assessed.map(s => parseFloat(s.total_score||0));
                    const avgScore  = scores.reduce((a,b)=>a+b,0)/scores.length;
                    const topClause = assessed.reduce((a,b) => parseFloat(a.total_score||0) > parseFloat(b.total_score||0) ? a : b);
                    const lowClause = assessed.reduce((a,b) => parseFloat(a.total_score||0) < parseFloat(b.total_score||0) ? a : b);
                    const topInfo   = CLAUSES.find(c => c.code === topClause.clause_code);
                    const lowInfo   = CLAUSES.find(c => c.code === lowClause.clause_code);

                    const totalMet     = responses.filter(r => r.fulfillment === 'Met').length;
                    const totalPartial = responses.filter(r => r.fulfillment === 'Partially Met').length;
                    const totalNotMet  = responses.filter(r => r.fulfillment === 'Not Met').length;
                    const highCritGaps = responses.filter(r =>
                        (r.criticality_of_observation||'').toLowerCase()==='high' &&
                        (r.fulfillment==='Not Met'||r.fulfillment==='Partially Met')
                    ).length;
                    const goodPractices = responses.filter(r => r.area_of_good_practice && r.area_of_good_practice.trim().length > 3).length;

                    const performanceWord = overall >= 90 ? 'strong' : overall >= 80 ? 'good' : overall >= 70 ? 'moderate' : overall >= 60 ? 'below the minimum acceptable' : 'poor';

                    return (
                        <div style={{ background:'#f0f7ff', border:'1px solid #c0d4f5', borderLeft:'4px solid #1890ff', borderRadius:6, padding:'12px 16px', marginBottom:20, fontSize:11, lineHeight:1.9, color:'#333' }}>
                            <div style={{ fontWeight:700, fontSize:12, color:'#1890ff', marginBottom:6 }}>AUDIT SUMMARY ANALYSIS</div>
                            <p style={{ margin:'0 0 6px' }}>
                                {facility.business_name} achieved an overall score of <strong style={{ color:rating.color }}>{overall.toFixed(1)}%</strong> ({rating.label}), reflecting <strong>{performanceWord}</strong> compliance with QoC standards. Across {assessed.length} assessed clause{assessed.length!==1?'s':''}, a total of {responses.length} measurable elements were evaluated: <span style={{ color:'#389e0d', fontWeight:700 }}>{totalMet} Met</span>, <span style={{ color:'#fa8c16', fontWeight:700 }}>{totalPartial} Partially Met</span>, and <span style={{ color:'#f5222d', fontWeight:700 }}>{totalNotMet} Not Met</span>.
                            </p>
                            {topInfo && lowInfo && (
                                <p style={{ margin:'0 0 6px' }}>
                                    The strongest performance was recorded in <strong style={{ color:topInfo.color }}>{topInfo.code} — {topInfo.title}</strong> at {parseFloat(topClause.total_score).toFixed(1)}%, while the greatest opportunity for improvement lies in <strong style={{ color:lowInfo.color }}>{lowInfo.code} — {lowInfo.title}</strong> at {parseFloat(lowClause.total_score).toFixed(1)}%.
                                </p>
                            )}
                            {highCritGaps > 0 && (
                                <p style={{ margin:'0 0 6px', color:'#cf1322', fontWeight:600 }}>
                                    ⚠ {highCritGaps} high-criticality gap{highCritGaps>1?'s were':' was'} identified across clauses, requiring immediate corrective action.
                                </p>
                            )}
                            {goodPractices > 0 && (
                                <p style={{ margin:0, color:'#389e0d' }}>
                                    ✓ {goodPractices} area{goodPractices>1?'s':''} of good practice {goodPractices>1?'were':'was'} documented — these should be sustained and shared as models of excellence.
                                </p>
                            )}
                        </div>
                    );
                })()}

                <div>
                    <div style={{ fontWeight:800, fontSize:13, color:'#1a1a2e', marginBottom:10, borderLeft:'4px solid #1890ff', paddingLeft:10 }}>AUDIT TEAM</div>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                        <thead>
                            <tr style={{ background:'#f5f5f5' }}>
                                {['Role','Name','Areas Covered','Date','Signature'].map(h=>(
                                    <th key={h} style={{ padding:'6px 10px', textAlign:'left', borderBottom:'1px solid #ddd' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ ...CELL, fontWeight:700 }}>Lead Auditor</td>
                                <td style={{ ...CELL }}>{assessment.assessor_name||'—'}</td>
                                <td style={{ ...CELL, color:'#888' }}>All clauses</td>
                                <td style={{ ...CELL }}>{moment(assessment.assessment_date).format('DD/MM/YYYY')}</td>
                                <td style={{ ...CELL, minWidth:120 }}></td>
                            </tr>
                            {[1,2,3].map(n=>(
                                <tr key={n}>
                                    <td style={{ ...CELL, color:'#aaa' }}>Auditor {n}</td>
                                    <td style={{ ...CELL, minWidth:140, borderBottom:'1px dotted #ccc' }}></td>
                                    <td style={{ ...CELL, minWidth:140, borderBottom:'1px dotted #ccc' }}></td>
                                    <td style={{ ...CELL, borderBottom:'1px dotted #ccc' }}></td>
                                    <td style={{ ...CELL, minWidth:120, borderBottom:'1px dotted #ccc' }}></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── ONE PAGE PER CLAUSE ── */}
            {CLAUSES.map(cl => {
                const score = clauseMap[cl.code];
                const mes   = meByClause[cl.code] || [];
                const pct   = score ? parseFloat(score.total_score||0) : null;
                const r     = pct !== null ? getRating(pct) : null;
                const A     = score?.total_audited || 0;
                const B     = A > 0 ? (100/A) : 0;
                return (
                    <div key={cl.code} className="report-page" style={{ padding:'28px 36px', pageBreakAfter:'always' }}>
                        <div style={{ background:`linear-gradient(135deg,${cl.color},${cl.color}cc)`, padding:'14px 20px', borderRadius:'8px 8px 0 0' }}>
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                                <div>
                                    <div style={{ fontSize:11, color:'rgba(255,255,255,0.7)', fontWeight:700, letterSpacing:1 }}>STANDARD {cl.num}</div>
                                    <div style={{ fontSize:16, fontWeight:900, color:'#fff' }}>{cl.code} — {cl.title}</div>
                                    <div style={{ fontSize:11, color:'rgba(255,255,255,0.75)', marginTop:2 }}>{facility.business_name} • {moment(assessment.assessment_date).format('DD MMMM YYYY')}</div>
                                </div>
                                {pct!==null&&(
                                    <div style={{ textAlign:'right' }}>
                                        <div style={{ fontSize:36, fontWeight:900, color:'#fff', lineHeight:1 }}>{pct.toFixed(1)}%</div>
                                        <div style={{ marginTop:4 }}><Stars count={r.stars} size={16}/></div>
                                        <div style={{ fontSize:12, color:'rgba(255,255,255,0.9)', fontWeight:700, marginTop:2 }}>{r.label}</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={{ background:'#f8faff', border:`1px solid ${cl.color}30`, borderRadius:'0 0 8px 8px', padding:'12px 20px', marginBottom:16 }}>
                            <Row gutter={16}>
                                {[
                                    ['A — AUDITED', A, '#333'],
                                    ['B (100/A)', B.toFixed(2), '#333'],
                                    ['✓ MET', score?.met_count||0, '#389e0d'],
                                    ['◑ PARTIAL', score?.partially_met_count||0, '#fa8c16'],
                                    ['✗ NOT MET', score?.not_met_count||0, '#f5222d'],
                                    ['NA / NC', `${score?.not_applicable_count||0}/${score?.nc_count||0}`, '#888'],
                                    ['TOTAL SCORE', pct!==null?`${pct.toFixed(2)}%`:'—', r?.color||'#333'],
                                ].map(([lbl,val,col])=>(
                                    <Col key={lbl} span={3}>
                                        <div style={{ fontSize:10, color:'#888', fontWeight:700 }}>{lbl}</div>
                                        <div style={{ fontSize:18, fontWeight:900, color:col }}>{val}</div>
                                    </Col>
                                ))}
                            </Row>
                        </div>

                        {/* ── Clause narrative summary ── */}
                        {(() => {
                            const narrative = buildClauseNarrative(cl.code, cl.color, mes, score);
                            return narrative ? (
                                <div style={{ background:`${cl.color}0d`, border:`1px solid ${cl.color}40`, borderLeft:`4px solid ${cl.color}`, borderRadius:6, padding:'10px 14px', marginBottom:14, fontSize:11, lineHeight:1.85, color:'#333' }}>
                                    {narrative}
                                </div>
                            ) : null;
                        })()}

                        {mes.length > 0 ? (
                            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:10 }}>
                                <thead>
                                    <tr style={{ background:`${cl.color}18` }}>
                                        {['Ref No','Measurable Element','Fulfillment','Comment','General Observation','Criticality','Good Practice'].map(h=>(
                                            <th key={h} style={{ padding:'6px 8px', textAlign: h==='Ref No'||h==='Fulfillment'||h==='Criticality'?'center':'left', borderBottom:`2px solid ${cl.color}50`, fontWeight:700 }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {mes.map((me,idx)=>{
                                        const fs = FULFILLMENT_STYLE[me.fulfillment] || {};
                                        const isKCS = me.is_critical === 1;
                                        const isLow = me.fulfillment==='Not Met'||me.fulfillment==='Partially Met';
                                        return (
                                            <tr key={me.id||idx} style={{ background: isKCS&&isLow?'#fff0f0':isLow?'#fffcf0':idx%2?'#fafafa':'#fff' }}>
                                                <td style={{ ...CELL, textAlign:'center', fontFamily:'monospace', fontWeight:700, color:isKCS?'#f5222d':cl.color }}>{isKCS&&<span style={{ fontSize:9, marginRight:2 }}>⚠</span>}{me.ref_no}</td>
                                                <td style={{ ...CELL }}>{me.me_text||me.question_text}</td>
                                                <td style={{ ...CELL, textAlign:'center' }}>
                                                    {me.fulfillment
                                                        ? <div style={{ display:'inline-block', padding:'2px 6px', borderRadius:4, background:fs.bg, color:fs.color, fontWeight:700, fontSize:10 }}>{fs.icon} {fs.short}</div>
                                                        : <span style={{ color:'#ddd' }}>—</span>}
                                                </td>
                                                <td style={{ ...CELL, fontSize:9, color:'#555' }}>{me.comment||''}</td>
                                                <td style={{ ...CELL, fontSize:9, color:'#555' }}>{me.general_observation||''}</td>
                                                <td style={{ ...CELL, textAlign:'center', fontSize:9 }}>
                                                    {me.criticality_of_observation&&(
                                                        <Tag style={{ fontSize:9, padding:'1px 5px', margin:0, border:'none',
                                                            background: me.criticality_of_observation==='high'?'#fff2f0':me.criticality_of_observation==='medium'?'#fff7e6':'#f6ffed',
                                                            color: me.criticality_of_observation==='high'?'#f5222d':me.criticality_of_observation==='medium'?'#fa8c16':'#389e0d' }}>
                                                            {me.criticality_of_observation}
                                                        </Tag>
                                                    )}
                                                </td>
                                                <td style={{ ...CELL, fontSize:9, color:'#52c41a' }}>{me.area_of_good_practice||''}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div style={{ padding:24, textAlign:'center', color:'#aaa', background:'#fafafa', borderRadius:8, border:'1px dashed #ddd', fontSize:12 }}>
                                No ME responses recorded for this clause
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ─── CONSOLIDATED REPORT DOCUMENT ─────────────────────────────────────────────
function ConsolidatedReportDocument({ data }) {
    const { period_label, facilities, clause_summaries, audit_teams, filterType, filterRegion, meResponses } = data;

    const getR = s => {
        const score = parseFloat(s||0);
        if (score >= 98)  return { label:'Outstanding',          stars:5, color:'#1565C0' };
        if (score >= 95)  return { label:'Excellent',            stars:4, color:'#2E7D32' };
        if (score >= 90)  return { label:'Good',                 stars:3, color:'#558B2F' };
        if (score >= 85)  return { label:'Fair',                 stars:2, color:'#FFFF00' };  // yellow
        if (score >= 70)  return { label:'Requires Improvement', stars:1, color:'#E65100' };
        return              { label:'Inadequate',                stars:0, color:'#B71C1C' };
    };

    // Group by type
    const hospitals  = facilities.filter(f => (f.site_type||'').toLowerCase().includes('hospital'));
    const healthCtrs = facilities.filter(f => (f.site_type||'').toLowerCase().includes('health centre') || (f.site_type||'').toLowerCase().includes('health center'));
    const clinics    = facilities.filter(f => !hospitals.includes(f) && !healthCtrs.includes(f));

    // Group by region
    const regions = [...new Set(facilities.map(f => f.located_region || f.region || 'Unknown'))].sort();

    const ratingCount = (stars) => facilities.filter(f => getR(f.overall_percentage).stars === stars).length;
    const avg = arr => arr.length ? arr.reduce((a,b)=>a+b,0)/arr.length : 0;
    const typeAvg = (arr) => avg(arr.map(f=>parseFloat(f.overall_percentage||0)));

    const cellStyle = { border:'1px solid #ddd', padding:'6px 8px', fontSize:11 };
    const hdrStyle  = { ...cellStyle, background:'#1f3864', color:'#fff', fontWeight:700 };

    // ── Coloured methodology table rows ────────────────────────────────────────
    const SCORING_GUIDE = [
        { range:'98-100%',  label:'Outstanding',          stars:'★★★★★', rec:'Ready for ISO accreditation',                rowBg:'#1565C0', rowText:'#fff' },
        { range:'95-97.9%', label:'Excellent',            stars:'★★★★',  rec:'Ready for ISO accreditation',                rowBg:'#2E7D32', rowText:'#fff' },
        { range:'90-94.9%', label:'Good',                 stars:'★★★',   rec:'Almost ready for ISO accreditation',         rowBg:'#558B2F', rowText:'#fff' },
        { range:'85-89.9%', label:'Fair',                 stars:'★★',    rec:'Somewhat ready for ISO accreditation',       rowBg:'#FFFF00', rowText:'#000' },
        { range:'70-84.9%', label:'Requires Improvement', stars:'★',     rec:'Not ready for ISO accreditation',            rowBg:'#E65100', rowText:'#fff' },
        { range:'<70%',     label:'Inadequate',           stars:'—',     rec:'Cause for concern — urgent action required', rowBg:'#B71C1C', rowText:'#fff' },
    ];

    // ── Bar chart component ─────────────────────────────────────────────────────
    const BarChart = ({ items, showRegion }) => {
        const sorted = [...items].sort((a,b) => parseFloat(b.overall_percentage||0) - parseFloat(a.overall_percentage||0));
        return (
            <div style={{ marginBottom:20 }}>
                {sorted.map(f => {
                    const score = parseFloat(f.overall_percentage || 0);
                    const r     = getR(score);
                    return (
                        <div key={f.id} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                            <div style={{ width:190, fontSize:10, fontWeight:600, flexShrink:0, textAlign:'right', paddingRight:6, lineHeight:1.3 }}>
                                {f.business_name}
                                {showRegion && <span style={{ color:'#888', fontWeight:400 }}> · {f.located_region||f.region||''}</span>}
                            </div>
                            <div style={{ flex:1, background:'#f0f0f0', borderRadius:3, height:20, position:'relative', overflow:'visible' }}>
                                <div style={{ width:`${Math.min(score,100)}%`, height:'100%', background:r.color, borderRadius:3 }}/>
                                <span style={{ position:'absolute', left:`${Math.min(score,100)+0.5}%`, top:'50%', transform:'translateY(-50%)', fontSize:10, fontWeight:700, color:'#333', whiteSpace:'nowrap' }}>
                                    {score.toFixed(1)}%
                                </span>
                            </div>
                            <div style={{ width:18, fontSize:9, flexShrink:0 }}>
                                {'★'.repeat(r.stars)}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // ── Extract ME insights for a given clause from all facility responses ─────
    const getMEInsights = (clauseCode) => {
        if (!meResponses || !meResponses[clauseCode]) return null;
        const mes = meResponses[clauseCode];

        // High criticality issues (not met or partially met with high criticality)
        const criticalGaps = mes.filter(m =>
            (m.fulfillment === 'Not Met' || m.fulfillment === 'Partially Met') &&
            m.criticality_of_observation === 'high' &&
            (m.general_observation || m.comment)
        ).slice(0, 3);

        // Good practices
        const goodPractices = mes.filter(m =>
            m.area_of_good_practice && m.area_of_good_practice.trim().length > 5
        ).slice(0, 3);

        // Common observations (most frequent unique non-null observations)
        const obsMap = {};
        mes.forEach(m => {
            const obs = (m.general_observation || m.comment || '').trim();
            if (obs.length > 8) {
                obsMap[obs] = (obsMap[obs] || 0) + 1;
            }
        });
        const commonObs = Object.entries(obsMap)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 2)
            .map(([obs]) => obs);

        // Frequently not-met elements
        const notMetMEs = mes.filter(m => m.fulfillment === 'Not Met' && m.me_text);
        const notMetMap = {};
        notMetMEs.forEach(m => {
            const txt = (m.me_text || '').trim().substring(0, 80);
            notMetMap[txt] = (notMetMap[txt] || 0) + 1;
        });
        const topNotMet = Object.entries(notMetMap)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 2)
            .map(([txt, cnt]) => ({ txt, cnt }));

        return { criticalGaps, goodPractices, commonObs, topNotMet };
    };

    // ── Flowing prose narrative matching the MOM reference style ─────────────
    // Pattern: "X% above 70%, Y% below. Good practices: ... Most facilities ...
    // Though some challenges ... There is a need for ..."
    const buildFlowingNarrative = (clauseCode, clauseName, facScores) => {
        const ins = getMEInsights(clauseCode);
        const n   = facScores.length;
        if (!n) return null;

        const above70    = facScores.filter(f => f.clause_score >= 70).length;
        const below70    = n - above70;
        const above70Pct = Math.round((above70 / n) * 100);
        const below70Pct = 100 - above70Pct;
        const avgSc      = avg(facScores.map(f => f.clause_score));
        const r          = getR(avgSc);

        // Para 1 — opening compliance sentence (mirrors "75% of the audited facilities...")
        let para1 = `${above70Pct}% of the audited health facilities scored at or above 70% on the ${clauseName} clause and ${below70Pct}% are below the 70% minimum acceptable standard, with a system average of ${avgSc.toFixed(1)}% (${r.label}).`;

        // Para 2 — good practices, woven naturally ("The good practices identified are that...")
        let para2 = '';
        if (ins && ins.goodPractices.length > 0) {
            const gpTexts = ins.goodPractices
                .map(g => (g.area_of_good_practice||'').trim())
                .filter(t => t.length > 5);
            if (gpTexts.length > 0) {
                const sentences = gpTexts.map(t => t.charAt(0).toUpperCase() + t.slice(1).replace(/\.?$/, ''));
                para2 = `The good practices identified are that ${sentences[0].charAt(0).toLowerCase() + sentences[0].slice(1)}`;
                if (sentences.length > 1) para2 += `, ${sentences.slice(1).map(s => s.charAt(0).toLowerCase() + s.slice(1)).join(', ')}`;
                para2 += '.';
            }
        }

        // Para 3 — common positive observations ("Most facilities have good ... with well documented ...")
        let para3 = '';
        if (ins && ins.commonObs.length > 0) {
            const obsList = ins.commonObs.filter(o => o.length > 10).slice(0, 3);
            if (obsList.length > 0) {
                para3 = `Most of the health facilities ${obsList[0].charAt(0).toLowerCase() + obsList[0].slice(1)}`;
                if (!para3.trim().endsWith('.')) para3 += '.';
                if (obsList.length > 1) {
                    para3 += ` Though some facilities have challenges, ${obsList[1].charAt(0).toLowerCase() + obsList[1].slice(1)}`;
                    if (!para3.trim().endsWith('.')) para3 += '.';
                }
            }
        }

        // Para 4 — gaps/needs ("There is a need for...")
        let para4 = '';
        if (ins && ins.topNotMet.length > 0) {
            const gaps = ins.topNotMet.slice(0, 2);
            const gapDescs = gaps.map(g => g.txt.trim().replace(/[.…]+$/, '').toLowerCase());
            if (gapDescs.length === 1) {
                para4 = `There is a need for facilities to ${gapDescs[0]}.`;
            } else {
                para4 = `There is a need for facilities to ${gapDescs[0]}, and to ${gapDescs[1]}.`;
            }
        }

        // Para 5 — critical gaps (only if flagged high)
        let para5 = '';
        if (ins && ins.criticalGaps.length > 0) {
            const critTexts = ins.criticalGaps
                .map(g => (g.general_observation || g.comment || '').trim())
                .filter(t => t.length > 10).slice(0, 1);
            if (critTexts.length > 0) {
                para5 = `High-criticality concern: ${critTexts[0].charAt(0).toUpperCase() + critTexts[0].slice(1)}.`;
            }
        }

        return [para1, para2, para3, para4, para5].filter(Boolean).join(' ');
    };

    // ── Rich clause narrative with ME insights ────────────────────────────────
    const narrateClause = (clause, facScores) => {
        if (!facScores.length) return null;
        const scores  = facScores.map(f => f.clause_score);
        const avgSc   = avg(scores);
        const above80 = scores.filter(s => s >= 80).length;
        const below70 = scores.filter(s => s < 70).length;
        const highest = facScores.reduce((a,b) => a.clause_score > b.clause_score ? a : b);
        const lowest  = facScores.reduce((a,b) => a.clause_score < b.clause_score ? a : b);
        const hosp    = facScores.filter(f => (f.site_type||'').toLowerCase().includes('hospital'));
        const hc      = facScores.filter(f => (f.site_type||'').toLowerCase().includes('health centre') || (f.site_type||'').toLowerCase().includes('health center'));
        const cl      = facScores.filter(f => !hosp.includes(f) && !hc.includes(f));
        const hospAvg = avg(hosp.map(f=>f.clause_score));
        const hcAvg   = avg(hc.map(f=>f.clause_score));
        const clAvg   = avg(cl.map(f=>f.clause_score));

        let typeCompare = '';
        if (hosp.length && hc.length && cl.length) {
            const best  = hospAvg >= hcAvg && hospAvg >= clAvg ? 'hospitals' : hcAvg >= clAvg ? 'health centres' : 'clinics & PHUs';
            const worst = hospAvg <= hcAvg && hospAvg <= clAvg ? 'hospitals' : hcAvg <= clAvg ? 'health centres' : 'clinics & PHUs';
            typeCompare = ` Across facility types, ${best} performed best at ${Math.max(hospAvg,hcAvg,clAvg).toFixed(1)}%, while ${worst} recorded the lowest average at ${Math.min(hospAvg,hcAvg,clAvg).toFixed(1)}%.`;
        }

        const concern = below70 > 0 ? ` ${below70} facilit${below70>1?'ies':'y'} scored below 70%, indicating critical gaps that must be urgently addressed.` : '';

        // Opening stats sentence
        const opening = `Overall, ${above80} of ${facScores.length} facilities (${Math.round(above80/facScores.length*100)}%) scored above 80% on the ${clause.code} clause, with a system-wide average of ${avgSc.toFixed(1)}%. ` +
               `${highest.business_name} achieved the highest score at ${highest.clause_score.toFixed(1)}%, while ${lowest.business_name} recorded the lowest at ${lowest.clause_score.toFixed(1)}%.` +
               typeCompare + concern;

        // Flowing ME narrative
        const flowing = buildFlowingNarrative(clause.code, clause.title, facScores);

        return flowing ? `${opening} ${flowing}` : opening;
    };

    // ── Rich narrative for a group of facilities ───────────────────────────────
    const narrateGroup = (items, groupName, clauseCode) => {
        if (!items.length) return null;
        const scores  = items.map(f => parseFloat(f.overall_percentage||0));
        const avgSc   = avg(scores);
        const highest = items.reduce((a,b) => parseFloat(a.overall_percentage||0) > parseFloat(b.overall_percentage||0) ? a : b);
        const lowest  = items.reduce((a,b) => parseFloat(a.overall_percentage||0) < parseFloat(b.overall_percentage||0) ? a : b);
        const above80 = items.filter(f => parseFloat(f.overall_percentage||0) >= 80).length;
        const below70 = items.filter(f => parseFloat(f.overall_percentage||0) < 70).length;
        const r       = getR(avgSc);

        const performanceWord = avgSc >= 85 ? 'strong' : avgSc >= 75 ? 'moderate' : avgSc >= 65 ? 'below average' : 'poor';
        const spreadVal = parseFloat(highest.overall_percentage||0) - parseFloat(lowest.overall_percentage||0);
        const spreadWord = spreadVal > 20 ? 'wide variation' : spreadVal > 10 ? 'moderate variation' : 'relatively consistent performance';

        const base = `The ${items.length} ${groupName} recorded an average score of ${avgSc.toFixed(1)}%, indicating ${performanceWord} overall performance (${r.label}). ` +
               `${above80} of ${items.length} ${groupName} scored above 80%, while ${below70} fell below the 70% threshold — the minimum acceptable standard. ` +
               `Performance ranged from ${parseFloat(lowest.overall_percentage||0).toFixed(1)}% (${lowest.business_name}) to ${parseFloat(highest.overall_percentage||0).toFixed(1)}% (${highest.business_name}), reflecting ${spreadWord} across the group.` +
               (below70 > 0 ? ` The ${below70} ${groupName.endsWith('s') ? groupName.slice(0,-1) : groupName}${below70>1?'s':''} below 70% require${below70===1?'s':''} urgent targeted support and structured quality improvement plans.` :
               ` All ${groupName} met the minimum 70% threshold, which is a positive indicator of baseline compliance with quality of care standards.`);

        // If a clause code is provided, append flowing ME narrative
        if (clauseCode) {
            const clause = CLAUSES.find(c => c.code === clauseCode);
            const flowing = clause ? buildFlowingNarrative(clauseCode, clause.title, items.map(f => ({ ...f, clause_score: parseFloat(f.overall_percentage||0) }))) : null;
            if (flowing) return `${base} ${flowing}`;
        }

        return base;
    };

    // Cross-type comparison narrative
    const typeCompareNarrative = () => {
        if (!hospitals.length || !healthCtrs.length || !clinics.length) return null;
        const hAvg  = typeAvg(hospitals);
        const hcAvg = typeAvg(healthCtrs);
        const cAvg  = typeAvg(clinics);
        const types = [{name:'hospitals', avg:hAvg}, {name:'health centres', avg:hcAvg}, {name:'clinics & PHUs', avg:cAvg}];
        const best  = types.reduce((a,b) => a.avg > b.avg ? a : b);
        const worst = types.reduce((a,b) => a.avg < b.avg ? a : b);
        const mid   = types.find(t => t !== best && t !== worst);
        return `Comparing performance across facility types, ${best.name} led with an average of ${best.avg.toFixed(1)}%, followed by ${mid.name} at ${mid.avg.toFixed(1)}%, and ${worst.name} at ${worst.avg.toFixed(1)}%. ` +
               `The ${(best.avg - worst.avg).toFixed(1)} percentage point gap between the highest and lowest facility types suggests that ${worst.name} require more targeted interventions and resource allocation compared to their counterparts. ` +
               `This disparity may be attributed to differences in staffing levels, infrastructure, access to training, and management capacity.`;
    };

    const filterDesc = (filterType && filterType !== 'all' ? ` — ${filterType}` : '') +
                       (filterRegion && filterRegion !== 'all' ? ` — ${filterRegion} Region` : '');

    // ── Rating table rows component ────────────────────────────────────────────
    const RATING_TABLE = [
        { range:'98-100%',   label:'Outstanding',          stars:5, color:'#1565C0', bg:'#1565C0' },
        { range:'95-97.9%',  label:'Excellent',            stars:4, color:'#2E7D32', bg:'#2E7D32' },
        { range:'90-94.9%',  label:'Good',                 stars:3, color:'#558B2F', bg:'#558B2F' },
        { range:'85-89.9%',  label:'Fair',                 stars:2, color:'#FFFF00', bg:'#FFFF00' },  // yellow
        { range:'70-84.9%',  label:'Requires Improvement', stars:1, color:'#E65100', bg:'#E65100' },
        { range:'<70%',      label:'Inadequate',           stars:0, color:'#B71C1C', bg:'#B71C1C' },
    ];

    return (
        <div id="consolidated-report" style={{ fontFamily:"'Segoe UI',Arial,sans-serif", color:'#222', background:'#fff', width:'100%' }}>

            {/* ── COVER ── */}
            <div className="narrative-page" style={{ padding:'60px', display:'flex', flexDirection:'column', justifyContent:'space-between', background:'linear-gradient(160deg,#1f3864 0%,#2d6fa8 100%)' }}>
                <div style={{ textAlign:'center' }}>
                    <img src="assets/img/elogo.png" alt="MoH" style={{ width:110, marginBottom:32, filter:'brightness(10)' }}/>
                    <div style={{ color:'rgba(255,255,255,0.7)', fontSize:12, letterSpacing:3, textTransform:'uppercase', marginBottom:6 }}>Kingdom of Eswatini · Ministry of Health</div>
                    <div style={{ color:'rgba(255,255,255,0.7)', fontSize:12, letterSpacing:2, textTransform:'uppercase', marginBottom:48 }}>National Quality Management Programme</div>
                    <div style={{ color:'#fff', fontSize:42, fontWeight:900, lineHeight:1.1, marginBottom:12 }}>QUALITY OF CARE</div>
                    <div style={{ color:'#fff', fontSize:34, fontWeight:900, lineHeight:1.1, marginBottom:32 }}>COMPREHENSIVE AUDIT REPORT</div>
                    <div style={{ display:'flex', justifyContent:'center', gap:24, flexWrap:'wrap' }}>
                        {[
                            { label:'Period', val: period_label || 'FY 2024/25' },
                            { label:'Facilities Assessed', val: facilities.length },
                            { label:'Clauses Audited', val: 10 },
                            ...(filterType && filterType !== 'all' ? [{ label:'Filtered by Type', val: filterType }] : []),
                            ...(filterRegion && filterRegion !== 'all' ? [{ label:'Region', val: filterRegion }] : []),
                        ].map(item => (
                            <div key={item.label} style={{ background:'rgba(255,255,255,0.15)', backdropFilter:'blur(8px)', borderRadius:12, padding:'16px 24px', textAlign:'center', border:'1px solid rgba(255,255,255,0.3)' }}>
                                <div style={{ color:'rgba(255,255,255,0.6)', fontSize:10, textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>{item.label}</div>
                                <div style={{ color:'#fff', fontSize:24, fontWeight:800 }}>{item.val}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div style={{ textAlign:'center', color:'rgba(255,255,255,0.4)', fontSize:10 }}>
                    Generated {moment().format('DD MMMM YYYY')} · QoC Platform · Eswatini MoH
                </div>
            </div>

            {/* ── EXECUTIVE SUMMARY ── */}
            <div className="narrative-page" style={{ padding:'28px 40px' }}>
                <div style={{ fontSize:22, fontWeight:900, color:'#1f3864', borderBottom:'3px solid #1f3864', paddingBottom:8, marginBottom:20 }}>EXECUTIVE SUMMARY</div>
                {(() => {
                    const overallAvg = avg(facilities.map(f=>parseFloat(f.overall_percentage||0)));
                    const r = getR(overallAvg);
                    const star5=ratingCount(5), star4=ratingCount(4), star3=ratingCount(3), star2=ratingCount(2), star1=ratingCount(1), star0=ratingCount(0);
                    return (
                        <>
                            <p style={{ fontSize:12, lineHeight:1.9, marginBottom:14 }}>
                                The Ministry of Health through the Quality Management Program conducted quality of care audits across <strong>{facilities.length} healthcare facilities{filterDesc}</strong> during the <strong>{period_label}</strong> period. The audit evaluated all ten (10) QoC standard clauses, providing a comprehensive baseline of quality performance across the health system.
                            </p>
                            <p style={{ fontSize:12, lineHeight:1.9, marginBottom:14 }}>
                                The overall system-wide average score was <strong style={{ color:r.color }}>{overallAvg.toFixed(1)}%</strong>, placing the health system in the <strong style={{ color:r.color }}>{r.label}</strong> category overall. {overallAvg >= 80 ? 'This reflects a generally satisfactory level of compliance with quality of care standards, though significant variation exists between individual facilities.' : overallAvg >= 70 ? 'While above the minimum acceptable threshold, there is clear room for improvement across the system, particularly in documentation, emergency response, and infection control.' : 'This result is below the minimum acceptable standard of 70%, indicating that urgent system-wide interventions are required to bring facilities up to the required quality threshold.'}
                            </p>
                            {typeCompareNarrative() && (
                                <p style={{ fontSize:12, lineHeight:1.9, marginBottom:14 }}>{typeCompareNarrative()}</p>
                            )}

                            {/* ── STAR RATING FOR FACILITIES — proper coloured table ── */}
                            <div style={{ fontWeight:700, fontSize:13, color:'#1f3864', textDecoration:'underline', margin:'24px 0 8px' }}>STAR RATING FOR FACILITIES;</div>
                            <p style={{ fontSize:12, lineHeight:1.9, marginBottom:12 }}>
                                {facilities.length} healthcare facilities were audited and utilized star ratings to gauge their performance, as outlined on Table below. The colours of the bar graphs is a representation of the star rating in each of the healthcare facilities.
                            </p>
                            <div style={{ fontSize:12, fontStyle:'italic', fontWeight:600, marginBottom:6 }}>Table 4. Star Rating for facilities.</div>
                            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11, marginBottom:20 }}>
                                <thead>
                                    <tr>
                                        {['Score Range','Interpretation','Star Rating','Number of Facilities'].map(h=>(
                                            <th key={h} style={{ padding:'8px 10px', background:'#1f3864', color:'#fff', fontWeight:700, border:'1px solid #aaa', textAlign:'left' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        { range:'98-100%',  label:'Outstanding',          stars:5, rowBg:'#1565C0', rowText:'#fff' },
                                        { range:'95-97.9',  label:'Excellent',            stars:4, rowBg:'#2E7D32', rowText:'#fff' },
                                        { range:'90-94.5%', label:'Good',                 stars:3, rowBg:'#558B2F', rowText:'#fff' },
                                        { range:'85-89.9%', label:'Fair',                 stars:2, rowBg:'#FFFF00', rowText:'#000' },
                                        { range:'70-84.9%', label:'Needs Improvement',    stars:1, rowBg:'#E65100', rowText:'#fff' },
                                        { range:'>70%',     label:'Inadequate',           stars:0, rowBg:'#B71C1C', rowText:'#fff' },
                                    ].map((row,i)=>{
                                        const count = ratingCount(row.stars);
                                        return (
                                            <tr key={i}>
                                                <td style={{ border:'1px solid #aaa', padding:'6px 10px', background:row.rowBg, color:row.rowText, fontWeight:700 }}>{row.range}</td>
                                                <td style={{ border:'1px solid #aaa', padding:'6px 10px', background:row.rowBg, color:row.rowText, fontWeight:700 }}>{row.label}</td>
                                                <td style={{ border:'1px solid #aaa', padding:'6px 10px', fontSize:13, letterSpacing:2 }}>{'★'.repeat(row.stars)||''}</td>
                                                <td style={{ border:'1px solid #aaa', padding:'6px 10px', fontWeight:700, textAlign:'center' }}>{count}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            <p style={{ fontSize:12, lineHeight:1.9, color:'#444' }}>
                                {star5+star4 > 0 ? `${star5+star4} facilit${star5+star4>1?'ies':'y'} achieved 4 or more stars, demonstrating excellence in quality of care delivery. ` : ''}
                                {star3 > 0 ? `${star3} facilit${star3>1?'ies':'y'} achieved 3 stars (Good), indicating solid compliance approaching ISO readiness. ` : ''}
                                {star2 > 0 ? `${star2} facilit${star2>1?'ies':'y'} achieved 2 stars (Fair), reflecting moderate compliance with identifiable areas for improvement. ` : ''}
                                {star1 > 0 ? `${star1} facilit${star1>1?'ies':'y'} received 1 star (Requires Improvement), representing a significant portion of the audited facilities that need focused quality improvement efforts. ` : ''}
                                {star0 > 0 ? `${star0} facilit${star0>1?'ies':'y'} scored below 70% (Inadequate), requiring urgent intervention and structured corrective action plans. ` : ''}
                                {star1+star0 === 0 ? 'All audited facilities met the minimum 70% threshold, which represents a baseline achievement for the health system.' : ''}
                            </p>
                        </>
                    );
                })()}
            </div>

            {/* ── ABBREVIATIONS ── */}
            <div className="narrative-page" style={{ padding:'28px 40px' }}>
                <div style={{ fontSize:22, fontWeight:900, color:'#1f3864', borderBottom:'3px solid #1f3864', paddingBottom:8, marginBottom:20 }}>ABBREVIATIONS</div>
                <div style={{ columns:2, gap:40 }}>
                    {[['EHCP','Essential Health Care Package'],['ISO','International Organization for Standards'],
                      ['QoC','Quality of Care'],['NQMP','National Quality Management Program'],
                      ['SOP','Standard Operating Procedure'],['MoH','Ministry of Health'],
                      ['PHU','Primary Health Unit'],['SWASA','Eswatini Standards Authority'],
                      ['AACC','Access, Assessment and Continuity of Care'],['COP','Care of the Patient'],
                      ['MOM','Management of Medication'],['PRPE',"Patient's Rights and Patient's Education"],
                      ['IPCM','Infection Prevention and Control Management'],['PSQI','Patient Safety and Quality Improvement'],
                      ['GROM','Governance, Roles and Responsibilities of Management'],
                      ['FMS','Facility Management, Safety and Support Services'],
                      ['HRE','Human Resource and Education'],['ISM','Information System Management'],
                    ].map(([abbr, full]) => (
                        <div key={abbr} style={{ display:'flex', gap:12, padding:'3px 0', fontSize:11, breakInside:'avoid' }}>
                            <strong style={{ minWidth:50 }}>{abbr}</strong><span>{full}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── INTRODUCTION ── */}
            <div className="narrative-page" style={{ padding:'28px 40px' }}>
                <div style={{ fontSize:22, fontWeight:900, color:'#1f3864', borderBottom:'3px solid #1f3864', paddingBottom:8, marginBottom:20 }}>1. INTRODUCTION</div>
                <p style={{ fontSize:12, lineHeight:1.9, marginBottom:14 }}>
                    The Eswatini Ministry of Health strives to provide comprehensive, high-quality health services to the population. In line with this goal, the Ministry through the National Quality Management Program (NQMP) developed guidelines for the Eswatini QoC Standards, which are designed to deliver standardized care across all facility levels as categorized by the Essential Healthcare Package (EHCP II, 2017).
                </p>
                <p style={{ fontSize:12, lineHeight:1.9, marginBottom:14 }}>
                    The QoC standards come with an implementation framework which requires that a baseline assessment is conducted in each implementing facility. Remedial actions are then implemented through continuous quality improvement cycles to enable facilities to achieve higher scores in subsequent audits and ultimately attain ISO accreditation.
                </p>
                <p style={{ fontSize:12, lineHeight:1.9, marginBottom:20 }}>
                    This report presents audit findings for all <strong>{facilities.length}</strong> facilities{filterDesc} assessed during the <strong>{period_label}</strong> period.
                </p>

                <div style={{ fontSize:16, fontWeight:700, color:'#1f3864', marginBottom:8 }}>1.1 SCOPE OF THE AUDIT</div>
                <p style={{ fontSize:12, lineHeight:1.9, marginBottom:16 }}>
                    The audit focused on evaluating quality of care provided across all ten (10) standard clauses in {facilities.length} healthcare facilities. This included a comprehensive assessment of adherence to established standards across all service delivery areas.
                </p>

                <div style={{ fontSize:16, fontWeight:700, color:'#1f3864', marginBottom:8 }}>1.2 OBJECTIVES</div>
                {['Obtain a baseline on the quality-of-care standard.',
                  `Evaluate the current quality of care in the ${facilities.length} healthcare facilities and determine compliance with established standards.`,
                  'Identify strengths and areas requiring improvement across the health system.',
                  "Review how well facilities incorporate patient-centered care practices.",
                  'Encourage a culture of continuous quality improvement through actionable insights and recommendations.',
                ].map((obj, i) => (
                    <div key={i} style={{ display:'flex', gap:12, marginBottom:6, fontSize:12, lineHeight:1.7 }}>
                        <span style={{ minWidth:18, fontWeight:700 }}>{i+1}.</span><span>{obj}</span>
                    </div>
                ))}

                {/* Audited facilities grouped by type */}
                <div style={{ fontSize:15, fontWeight:700, color:'#1f3864', margin:'20px 0 10px' }}>AUDITED FACILITIES</div>
                {[
                    { label:'Hospitals', items: hospitals, color:'#1f3864' },
                    { label:'Health Centres', items: healthCtrs, color:'#0d6e6e' },
                    { label:'Clinics & PHUs', items: clinics, color:'#5b2d8e' },
                ].filter(g => g.items.length > 0).map(group => (
                    <div key={group.label} style={{ marginBottom:12 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:group.color, marginBottom:4 }}>
                            {group.label} ({group.items.length})
                        </div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                            {group.items.map(f => (
                                <div key={f.id} style={{ background:'#f0f5ff', border:'1px solid #c0d4f5', borderRadius:6, padding:'4px 10px', fontSize:11 }}>
                                    {f.business_name}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}

                {/* By region */}
                {regions.length > 1 && (
                    <>
                        <div style={{ fontSize:15, fontWeight:700, color:'#1f3864', margin:'16px 0 10px' }}>FACILITIES BY REGION</div>
                        {regions.map(region => {
                            const regFacs = facilities.filter(f => (f.located_region||f.region||'Unknown') === region);
                            return (
                                <div key={region} style={{ marginBottom:8, display:'flex', gap:12, alignItems:'baseline' }}>
                                    <strong style={{ minWidth:100, fontSize:12 }}>{region}:</strong>
                                    <span style={{ fontSize:11, color:'#555' }}>{regFacs.map(f=>f.business_name).join(', ')}</span>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>

            {/* ── METHODOLOGY ── */}
            <div className="narrative-page" style={{ padding:'28px 40px' }}>
                <div style={{ fontSize:22, fontWeight:900, color:'#1f3864', borderBottom:'3px solid #1f3864', paddingBottom:8, marginBottom:20 }}>2. METHODOLOGY</div>
                <p style={{ fontSize:12, lineHeight:1.9, marginBottom:14 }}>
                    The MoH quality-of-care audit tool was used to conduct audits across all ten (10) standard clauses. Each clause contains requirements and measurable elements rated as "Met", "Partially Met", or "Not Met". Scores are computed per requirement and aggregated to yield a clause-level and overall facility percentage score.
                </p>

                {/* ── COLOURED scoring guide table matching the template ── */}
                <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>Table 1: Audit Scoring Guide</div>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11, marginBottom:20 }}>
                    <thead>
                        <tr>
                            {['Score Range','Interpretation','Star Rating','Recommendation'].map(h=>(
                                <th key={h} style={{ ...hdrStyle, padding:'8px 12px' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {SCORING_GUIDE.map((row, i) => (
                            <tr key={i}>
                                <td style={{ border:'1px solid #ccc', padding:'7px 12px', fontSize:11,
                                    background:row.rowBg, color:row.rowText, fontWeight:700 }}>{row.range}</td>
                                <td style={{ border:'1px solid #ccc', padding:'7px 12px', fontSize:11,
                                    background:row.rowBg, color:row.rowText, fontWeight:700 }}>{row.label}</td>
                                <td style={{ border:'1px solid #ccc', padding:'7px 12px', fontSize:13,
                                    background:row.rowBg, color:row.rowText, textAlign:'center' }}>{row.stars}</td>
                                <td style={{ border:'1px solid #ccc', padding:'7px 12px', fontSize:11,
                                    background:'#fff', color:'#222' }}>{row.rec}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* ── AUDIT TEAM — API data only, always show roles & process ── */}
                {(() => {
                    const teamRows = (audit_teams && audit_teams.length > 0)
                        ? audit_teams.map(m => ({ name: m.user_name||m.name||'', team: m.region||m.team||'' }))
                        : [];

                    return (
                        <>
                            {teamRows.length > 0 && (
                                <>
                                    <p style={{ fontSize:12, lineHeight:1.9, marginBottom:14 }}>
                                        The audit teams comprised of the following: collectively possessing diverse expertise in healthcare quality management, patient care, and regulatory compliance. This multidisciplinary team was carefully selected to ensure comprehensive coverage of the audit objectives.
                                    </p>
                                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11, marginBottom:16 }}>
                                        <thead>
                                            <tr>
                                                <th style={{ ...hdrStyle, padding:'8px 12px' }}>Name &amp; Surname</th>
                                                <th style={{ ...hdrStyle, padding:'8px 12px' }}>Team</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {teamRows.map((m,i)=>(
                                                <tr key={i} style={{ background: i%2?'#f5f5f5':'#fff' }}>
                                                    <td style={{ ...cellStyle, padding:'6px 12px' }}>{m.name}</td>
                                                    <td style={{ ...cellStyle, padding:'6px 12px' }}>{m.team}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}

                            <p style={{ fontSize:12, lineHeight:1.9, marginBottom:8 }}>
                                <strong>Their roles and responsibilities include;</strong>
                            </p>
                            <p style={{ fontSize:12, lineHeight:1.9, marginBottom:8 }}>
                                <strong>Audit Team Lead</strong> — Planning the audit, determining audit schedule, determining team size, assigning workload to the audit team, leading the proceedings of the audit and preparing the Audit Report.
                            </p>
                            <p style={{ fontSize:12, lineHeight:1.9, marginBottom:16 }}>
                                <strong>Auditors</strong> — Audit planning with lead auditor, audit execution within scope of audit, documenting audit findings and reporting to lead auditor, follow up and verification of corrective actions on non-conformances.
                            </p>
                            <p style={{ fontSize:12, lineHeight:1.9, marginBottom:16 }}>
                                The audit process involved a combination of on-site inspections, document reviews, and breakaway sessions. During breakaway sessions, sub-teams visited the relevant departments and consultation rooms to engage with relevant facility personnel and stakeholders, observe, gather insights and exchange information relevant to the audit questions. Following the breakaway sessions, sub-teams reconvened to consolidate their findings and ensure completeness of the audit, consistency across audited clauses — this included cross-referencing data, triangulating information, and conducting peer reviews within the audit team.
                            </p>
                            <p style={{ fontSize:12, lineHeight:1.9 }}>
                                Table 3 below shows audited facilities on Quality of Care standard:
                            </p>
                        </>
                    );
                })()}
            </div>

            {/* ── FINDINGS — OVERALL ── */}
            <div className="narrative-page" style={{ padding:'28px 40px' }}>
                <div style={{ fontSize:22, fontWeight:900, color:'#1f3864', borderBottom:'3px solid #1f3864', paddingBottom:8, marginBottom:20 }}>3. FINDINGS</div>

                <div style={{ fontSize:17, fontWeight:800, color:'#1f3864', marginBottom:14 }}>3.1 FINDINGS &amp; DISCUSSIONS</div>

                {/* ── Data-driven narrative matching the reference style ── */}
                {(() => {
                    const n          = facilities.length;
                    const star0      = ratingCount(0);
                    const star1      = ratingCount(1);
                    const star2      = ratingCount(2);
                    const star3      = ratingCount(3);
                    const star4      = ratingCount(4);
                    const star5      = ratingCount(5);
                    const pct1star   = Math.round((star1 / n) * 100);
                    const pct0       = Math.round((star0 / n) * 100);
                    const inadequate = star0;
                    const needsImp   = star1;
                    const overallAvg = avg(facilities.map(f => parseFloat(f.overall_percentage||0)));
                    const r          = getR(overallAvg);

                    // Figure references — count which figures actually exist
                    const figCount = [hospitals.length > 0, healthCtrs.length > 0, clinics.length > 0].filter(Boolean).length;
                    const figRef   = figCount === 1 ? 'Figure 1' : figCount === 2 ? 'Figures 1 &amp; 2' : 'Figures 1, 2 &amp; 3';

                    // Build star distribution sentence
                    const starSentences = [];
                    if (star5 > 0) starSentences.push(`${star5} facilit${star5>1?'ies':'y'} achieved a five-star rating (Outstanding), demonstrating exceptional adherence to quality of care standards.`);
                    if (star4 > 0) starSentences.push(`${star4} facilit${star4>1?'ies':'y'} achieved a four-star rating (Excellent), reflecting strong performance and readiness for ISO accreditation.`);
                    if (star3 > 0) starSentences.push(`${star3===1?'Only one facility':'Only '+star3+' facilities'} attained a three-star rating, demonstrating good performance and adherence to quality care standards.`);
                    if (star2 > 0) starSentences.push(`${star2===1?'One facility':'Two facilities'} achieved a two-star rating (Fair), reflecting a moderate level of compliance but still indicating areas needing improvement.`);
                    if (star1 > 0) starSentences.push(`Over ${pct1star}% of the facilities received a one-star rating (Requires Improvement), indicating a significant number of facilities are currently below the desired standard and require substantial improvements to enhance compliance with quality of care standards.`);
                    if (star0 > 0) starSentences.push(`${inadequate} facilit${inadequate>1?'ies':'y'} scored below 70% and ${inadequate>1?'were':'was'} rated Inadequate, representing a cause for concern that requires urgent intervention and corrective action.`);

                    return (
                        <>
                            <p style={{ fontSize:12, lineHeight:1.9, marginBottom:12 }}>
                                The audit assessed <strong>{n} healthcare facilities</strong> and utilised star ratings to gauge their performance, as outlined in Table 1 of the methodology section. The colours of the bar graphs are a representation of the star rating in each of the healthcare facilities. The system-wide average score was <strong style={{ color:r.color }}>{overallAvg.toFixed(1)}%</strong>, placing the health system in the <strong style={{ color:r.color }}>{r.label}</strong> category overall.
                            </p>

                            {starSentences.map((s, i) => (
                                <p key={i} style={{ fontSize:12, lineHeight:1.9, marginBottom:10 }}>{s}</p>
                            ))}

                            {(needsImp > 0 || inadequate > 0) && (
                                <p style={{ fontSize:12, lineHeight:1.9, marginBottom:12 }}>
                                    The majority of facilities, falling into the one-star and inadequate categories, need focused efforts to address the gaps in their compliance with quality standards. This includes enhancing documentation practices, improving infection control measures, and increasing overall staff training and resources.
                                </p>
                            )}

                            <p style={{ fontSize:12, lineHeight:1.9, marginBottom:20 }}>
                                The audit results as shown in {figRef}, highlight a broad spectrum of performance across the {n} facilities, with a clear need for targeted improvements to elevate the quality of care. The limited number of high ratings underscores the importance of addressing deficiencies and implementing comprehensive strategies to enhance overall compliance and performance.
                            </p>
                        </>
                    );
                })()}

                {/* Type comparison summary boxes */}
                <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                    {[
                        { label:'All Facilities', items: facilities, color:'#1f3864' },
                        hospitals.length > 0    && { label:'Hospitals',       items: hospitals,  color:'#0d6e6e' },
                        healthCtrs.length > 0   && { label:'Health Centres',  items: healthCtrs, color:'#5b2d8e' },
                        clinics.length > 0      && { label:'Clinics & PHUs',  items: clinics,    color:'#c0392b' },
                    ].filter(Boolean).map(group => {
                        const sc = typeAvg(group.items);
                        const r  = getR(sc);
                        return (
                            <div key={group.label} style={{ flex:1, minWidth:120, background:'#f8faff', border:`2px solid ${group.color}`, borderRadius:10, padding:'12px', textAlign:'center' }}>
                                <div style={{ fontSize:10, color:'#888', marginBottom:2 }}>{group.label} ({group.items.length})</div>
                                <div style={{ fontSize:26, fontWeight:900, color:r.color }}>{sc.toFixed(1)}%</div>
                                <div style={{ fontSize:10, color:r.color }}>{r.label}</div>
                                <div style={{ fontSize:12 }}>{'★'.repeat(r.stars)||'—'}</div>
                            </div>
                        );
                    })}
                </div>

                {/* Cross-type narrative */}
                {typeCompareNarrative() && (
                    <p style={{ fontSize:12, lineHeight:1.9, marginBottom:16, background:'#f0f7ff', padding:'12px 16px', borderRadius:8, borderLeft:'4px solid #1f3864' }}>
                        {typeCompareNarrative()}
                    </p>
                )}

                {/* Graphs — one per facility type */}
                {hospitals.length > 0 && (
                    <div style={{ marginBottom:24 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:'#0d6e6e', marginBottom:8 }}>Figure 1: Overall Scores — Hospitals</div>
                        <BarChart items={hospitals}/>
                        <p style={{ fontSize:11, lineHeight:1.8, color:'#444', marginTop:10, paddingLeft:4 }}>{narrateGroup(hospitals, 'hospitals', null)}</p>
                    </div>
                )}
                {healthCtrs.length > 0 && (
                    <div style={{ marginBottom:24 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:'#5b2d8e', marginBottom:8 }}>Figure 2: Overall Scores — Health Centres</div>
                        <BarChart items={healthCtrs}/>
                        <p style={{ fontSize:11, lineHeight:1.8, color:'#444', marginTop:10, paddingLeft:4 }}>{narrateGroup(healthCtrs, 'health centres', null)}</p>
                    </div>
                )}
                {clinics.length > 0 && (
                    <div style={{ marginBottom:24 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:'#c0392b', marginBottom:8 }}>Figure 3: Overall Scores — Clinics & PHUs</div>
                        <BarChart items={clinics}/>
                        <p style={{ fontSize:11, lineHeight:1.8, color:'#444', marginTop:10, paddingLeft:4 }}>{narrateGroup(clinics, 'clinics & PHUs', null)}</p>
                    </div>
                )}

                {/* Regional overview chart stays in main findings page */}
                {regions.length > 1 && (
                    <div style={{ marginBottom:24 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:'#1f3864', marginBottom:12 }}>Figure 4: Overall Scores by Region (Summary)</div>
                        {regions.map((region) => {
                            const regFacs = facilities.filter(f => (f.located_region||f.region||'Unknown') === region);
                            const regAvg  = typeAvg(regFacs);
                            const rr      = getR(regAvg);
                            return (
                                <div key={region} style={{ marginBottom:16 }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                                        <div style={{ fontSize:12, fontWeight:700, color:'#1f3864' }}>{region} Region</div>
                                        <div style={{ fontSize:11, fontWeight:700, color:rr.color }}>avg {regAvg.toFixed(1)}% — {regFacs.length} facilities</div>
                                    </div>
                                    <BarChart items={regFacs}/>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ── 3.2 REGIONAL PERFORMANCE — dedicated pages per region (ASRH style) ── */}
            {regions.length > 0 && (() => {
                const REGION_ORDER = ['Hhohho','Manzini','Lubombo','Shiselweni'];
                const sortedRegions = [...regions].sort((a,b) => {
                    const ai = REGION_ORDER.findIndex(r => a.toLowerCase().includes(r.toLowerCase()));
                    const bi = REGION_ORDER.findIndex(r => b.toLowerCase().includes(r.toLowerCase()));
                    if (ai === -1 && bi === -1) return a.localeCompare(b);
                    if (ai === -1) return 1; if (bi === -1) return -1;
                    return ai - bi;
                });
                const REGION_COLORS = { 'Hhohho':'#1f3864','Manzini':'#0d6e6e','Lubombo':'#5b2d8e','Shiselweni':'#c0392b' };
                const getRegCol = (r) => {
                    const key = REGION_ORDER.find(k => r.toLowerCase().includes(k.toLowerCase()));
                    return REGION_COLORS[key] || '#444';
                };

                // Cross-region comparison paragraph (ASRH methodology style)
                const regionStats = sortedRegions.map(r => {
                    const facs = facilities.filter(f=>(f.located_region||f.region||'Unknown')===r);
                    return { region:r, avg:typeAvg(facs), n:facs.length,
                        above70:facs.filter(f=>parseFloat(f.overall_percentage||0)>=70).length,
                        below70:facs.filter(f=>parseFloat(f.overall_percentage||0)<70).length };
                });
                const rsBest  = [...regionStats].sort((a,b)=>b.avg-a.avg)[0];
                const rsWorst = [...regionStats].sort((a,b)=>a.avg-b.avg)[0];
                const rsMid   = regionStats.filter(r=>r!==rsBest&&r!==rsWorst).map(r=>`${r.region} (${r.avg.toFixed(1)}%)`).join(', ');

                const crossNarrative = sortedRegions.length > 1
                    ? `Comparing performance across the regions, ${rsBest.region} leads with an average score of ${rsBest.avg.toFixed(1)}%${rsMid ? `, followed by ${rsMid},` : ','} while ${rsWorst.region} recorded the lowest regional average at ${rsWorst.avg.toFixed(1)}%. The ${(rsBest.avg - rsWorst.avg).toFixed(1)} percentage point gap between the highest and lowest performing regions highlights disparities in healthcare quality delivery across the country, likely influenced by differences in facility infrastructure, staffing levels, and management capacity.${rsWorst.below70 > 0 ? ` ${rsWorst.region} requires prioritised support, with ${rsWorst.below70} facilit${rsWorst.below70>1?'ies':'y'} scoring below the 70% minimum threshold.` : ''}`
                    : null;

                return (
                    <>
                        {/* Overview comparison page */}
                        <div className="narrative-page" style={{ padding:'28px 40px' }}>
                            <div style={{ fontSize:19, fontWeight:900, color:'#1f3864', borderBottom:'3px solid #1f3864', paddingBottom:8, marginBottom:16 }}>3.2 REGIONAL PERFORMANCE</div>
                            <p style={{ fontSize:12, lineHeight:1.9, marginBottom:14 }}>
                                This section presents the quality of care performance across Eswatini's four regions: Hhohho, Manzini, Lubombo, and Shiselweni. Regional performance is analysed using bar graphs and supported by narrative analysis in line with the national quality assessment methodology. The colours of the bar graphs represent the star rating achieved by each facility.
                            </p>
                            <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
                                {regionStats.map(rs => {
                                    const rc  = getR(rs.avg);
                                    const col = getRegCol(rs.region);
                                    return (
                                        <div key={rs.region} style={{ flex:1, minWidth:110, background:'#f8faff', border:`2px solid ${col}`, borderRadius:10, padding:'12px', textAlign:'center' }}>
                                            <div style={{ fontSize:11, fontWeight:700, color:col, marginBottom:2 }}>{rs.region}</div>
                                            <div style={{ fontSize:10, color:'#aaa', marginBottom:4 }}>{rs.n} facilities</div>
                                            <div style={{ fontSize:24, fontWeight:900, color:rc.color }}>{rs.avg.toFixed(1)}%</div>
                                            <div style={{ fontSize:10, color:rc.color }}>{rc.label}</div>
                                            <div style={{ fontSize:11, color:rc.color }}>{'★'.repeat(rc.stars)||'—'}</div>
                                        </div>
                                    );
                                })}
                            </div>
                            {crossNarrative && (
                                <p style={{ fontSize:12, lineHeight:1.9, background:'#f0f7ff', padding:'12px 16px', borderRadius:8, borderLeft:'4px solid #1f3864' }}>
                                    {crossNarrative}
                                </p>
                            )}
                        </div>

                        {/* One full page per region */}
                        {sortedRegions.map((region, ri) => {
                            const regFacs = facilities.filter(f=>(f.located_region||f.region||'Unknown')===region);
                            const regAvg  = typeAvg(regFacs);
                            const rc      = getR(regAvg);
                            const col     = getRegCol(region);
                            const above70 = regFacs.filter(f=>parseFloat(f.overall_percentage||0)>=70).length;
                            const above80 = regFacs.filter(f=>parseFloat(f.overall_percentage||0)>=80).length;
                            const below70 = regFacs.length - above70;
                            const sorted  = [...regFacs].sort((a,b)=>parseFloat(b.overall_percentage||0)-parseFloat(a.overall_percentage||0));
                            const top3    = sorted.slice(0,3).map(f=>`${f.business_name} (${parseFloat(f.overall_percentage||0).toFixed(1)}%)`);
                            const bot3    = below70 > 0 ? sorted.slice(-Math.min(3,below70)).map(f=>`${f.business_name} (${parseFloat(f.overall_percentage||0).toFixed(1)}%)`) : [];
                            const pct70   = Math.round((above70/regFacs.length)*100);
                            const star0   = regFacs.filter(f=>getR(f.overall_percentage).stars===0).length;
                            const star1   = regFacs.filter(f=>getR(f.overall_percentage).stars===1).length;
                            const star2   = regFacs.filter(f=>getR(f.overall_percentage).stars===2).length;
                            const star3up = regFacs.filter(f=>getR(f.overall_percentage).stars>=3).length;

                            // Per-region narrative (ASRH style)
                            let regNarrative = `The ${region} region had ${regFacs.length} facilit${regFacs.length>1?'ies':'y'} assessed during this audit period, recording a regional average of ${regAvg.toFixed(1)}% (${rc.label}). `;
                            regNarrative += `${pct70}% of ${region} facilities (${above70} of ${regFacs.length}) met or exceeded the 70% minimum compliance threshold${below70>0?`, while ${below70} fell below this standard and require targeted quality improvement interventions`:''}. `;
                            if (above80 > 0) regNarrative += `${above80} facilit${above80>1?'ies':'y'} scored above 80%, reflecting commendable performance. `;
                            if (top3.length > 0) regNarrative += `The strongest performing ${top3.length>1?'facilities were':'facility was'} ${top3.join(', ')}. `;
                            if (bot3.length > 0) regNarrative += `Facilities scoring below 70% — ${bot3.join(', ')} — require structured corrective action plans and intensified mentorship to address identified gaps.`;

                            // Clause breakdown for this region
                            const clauseRows = CLAUSES.map(cl => {
                                const scores = regFacs.map(f=>parseFloat(f.clause_scores?.[cl.code]||0)).filter(s=>s>0);
                                if (!scores.length) return null;
                                const cavg = scores.reduce((a,b)=>a+b,0)/scores.length;
                                return { ...cl, cavg, cr: getR(cavg) };
                            }).filter(Boolean);
                            const topClause = clauseRows.length ? clauseRows.reduce((a,b)=>a.cavg>b.cavg?a:b) : null;
                            const botClause = clauseRows.length ? clauseRows.reduce((a,b)=>a.cavg<b.cavg?a:b) : null;
                            const clauseNote = topClause && botClause && topClause.code !== botClause.code
                                ? `Within ${region}, the strongest clause was ${topClause.code} — ${topClause.title} at ${topClause.cavg.toFixed(1)}%, while ${botClause.code} — ${botClause.title} recorded the lowest average at ${botClause.cavg.toFixed(1)}%, representing a priority area for improvement.`
                                : null;

                            return (
                                <div key={region} className="narrative-page" style={{ padding:'28px 40px' }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:4 }}>
                                        <div style={{ background:col, color:'#fff', borderRadius:8, padding:'4px 16px', fontWeight:900, fontSize:13 }}>{region}</div>
                                        <div style={{ fontSize:16, fontWeight:900, color:'#1f3864' }}>Regional Performance</div>
                                    </div>
                                    <div style={{ height:3, background:`linear-gradient(90deg,${col},transparent)`, marginBottom:14, borderRadius:2 }}/>

                                    {/* Stats strip */}
                                    <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
                                        {[
                                            { label:'Facilities',   val:regFacs.length,          color:'#1f3864'  },
                                            { label:'Regional Avg', val:`${regAvg.toFixed(1)}%`,  color:rc.color   },
                                            { label:'Above 70%',    val:above70,                  color:'#2E7D32'  },
                                            { label:'Below 70%',    val:below70,                  color:'#B71C1C'  },
                                            { label:'3+ Stars',     val:star3up,                  color:'#2E7D32'  },
                                            { label:'2 Stars (Fair)',val:star2,                   color:'#B8860B'  },
                                            { label:'1 Star',       val:star1,                    color:'#E65100'  },
                                            { label:'Inadequate',   val:star0,                    color:'#B71C1C'  },
                                        ].map(s => (
                                            <div key={s.label} style={{ background:'#f8faff', border:'1px solid #e0eaff', borderRadius:8, padding:'5px 8px', textAlign:'center', flex:1, minWidth:65 }}>
                                                <div style={{ fontSize:8, color:'#888', marginBottom:1 }}>{s.label}</div>
                                                <div style={{ fontSize:14, fontWeight:800, color:s.color }}>{s.val}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Bar chart */}
                                    <div style={{ fontSize:12, fontWeight:700, color:col, marginBottom:6 }}>
                                        Figure {ri+5}: Overall Scores — {region} Region
                                    </div>
                                    <BarChart items={regFacs}/>

                                    {/* Region narrative */}
                                    <p style={{ fontSize:11, lineHeight:1.85, color:'#333', marginTop:10, marginBottom:10, background:`${col}0d`, padding:'10px 14px', borderRadius:6, borderLeft:`4px solid ${col}` }}>
                                        {regNarrative}
                                    </p>
                                    {clauseNote && (
                                        <p style={{ fontSize:11, lineHeight:1.85, color:'#444', marginBottom:12 }}>{clauseNote}</p>
                                    )}

                                    {/* Clause mini-scorecard */}
                                    {clauseRows.length > 0 && (
                                        <>
                                            <div style={{ fontSize:11, fontWeight:700, color:'#1f3864', marginBottom:6 }}>Clause Performance — {region} Region</div>
                                            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:9.5 }}>
                                                <thead>
                                                    <tr style={{ background:'#1f3864' }}>
                                                        {['Code','Clause Title','Avg Score','Rating'].map(h=>(
                                                            <th key={h} style={{ padding:'4px 8px', color:'#fff', fontWeight:700, textAlign:'left', border:'1px solid #aaa' }}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {clauseRows.map((cl,ci2) => (
                                                        <tr key={cl.code} style={{ background:ci2%2?'#f5f5f5':'#fff' }}>
                                                            <td style={{ padding:'3px 8px', border:'1px solid #ddd', fontWeight:700, color:cl.color }}>{cl.code}</td>
                                                            <td style={{ padding:'3px 8px', border:'1px solid #ddd' }}>{cl.title}</td>
                                                            <td style={{ padding:'3px 8px', border:'1px solid #ddd', fontWeight:700, color:cl.cr.color, textAlign:'center' }}>{cl.cavg.toFixed(1)}%</td>
                                                            <td style={{ padding:'3px 8px', border:'1px solid #ddd', color:cl.cr.color }}>{'★'.repeat(cl.cr.stars)||'—'} {cl.cr.label}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </>
                );
            })()}

            {/* ── PER CLAUSE FINDINGS ── */}
            {CLAUSES.map((clause, ci) => {
                const facScores = facilities.map(f => ({
                    ...f,
                    clause_score: parseFloat(f.clause_scores?.[clause.code] || 0),
                })).filter(f => f.clause_score > 0);

                if (facScores.length === 0) return null;

                const clauseAvg   = avg(facScores.map(f=>f.clause_score));
                const rClause     = getR(clauseAvg);
                const hospScores  = facScores.filter(f => (f.site_type||'').toLowerCase().includes('hospital'));
                const hcScores    = facScores.filter(f => (f.site_type||'').toLowerCase().includes('health centre') || (f.site_type||'').toLowerCase().includes('health center'));
                const clinScores  = facScores.filter(f => !hospScores.includes(f) && !hcScores.includes(f));

                // ME insights for this clause
                const clauseIns = getMEInsights(clause.code);

                return (
                    <div key={clause.code} className="narrative-page" style={{ padding:'28px 40px' }}>
                        {/* Clause header */}
                        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                            <div style={{ background:clause.color, color:'#fff', borderRadius:8, padding:'4px 14px', fontWeight:900, fontSize:13 }}>{clause.code}</div>
                            <div style={{ fontSize:17, fontWeight:900, color:'#1f3864' }}>Clause {clause.num}: {clause.title.toUpperCase()}</div>
                        </div>
                        <div style={{ height:3, background:`linear-gradient(90deg, ${clause.color}, transparent)`, marginBottom:16, borderRadius:2 }}/>

                        {/* Stats strip */}
                        <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
                            {[
                                { label:'System Average', val:`${clauseAvg.toFixed(1)}%`, color:rClause.color },
                                { label:'Facilities Assessed', val:facScores.length },
                                { label:'Above 80%', val:facScores.filter(f=>f.clause_score>=80).length, color:'#52c41a' },
                                { label:'Below 70%', val:facScores.filter(f=>f.clause_score<70).length, color:'#f5222d' },
                                { label:'Highest', val:`${Math.max(...facScores.map(f=>f.clause_score)).toFixed(1)}%`, color:'#389e0d' },
                                { label:'Lowest',  val:`${Math.min(...facScores.map(f=>f.clause_score)).toFixed(1)}%`, color:'#f5222d' },
                            ].map(s => (
                                <div key={s.label} style={{ background:'#f8faff', border:'1px solid #e0eaff', borderRadius:8, padding:'8px 12px', textAlign:'center', flex:1, minWidth:80 }}>
                                    <div style={{ fontSize:9, color:'#888', marginBottom:2 }}>{s.label}</div>
                                    <div style={{ fontSize:15, fontWeight:800, color:s.color||'#1f3864' }}>{s.val}</div>
                                </div>
                            ))}
                        </div>

                        {/* Clause narrative referencing the graph */}
                        <p style={{ fontSize:12, lineHeight:1.9, color:'#333', marginBottom:16, background:'#f9f0ff', padding:'12px 16px', borderRadius:8, borderLeft:`4px solid ${clause.color}` }}>
                            {narrateClause(clause, facScores)}
                        </p>

                        {/* Graphs — by facility type */}
                        {hospScores.length > 0 && (
                            <div style={{ marginBottom:16 }}>
                                <div style={{ fontSize:12, fontWeight:700, color:'#0d6e6e', marginBottom:6 }}>
                                    Figure {ci*3+4}: {clause.code} — Hospitals
                                </div>
                                <BarChart items={hospScores.map(f=>({...f, overall_percentage:f.clause_score}))}/>
                                <p style={{ fontSize:11, lineHeight:1.8, color:'#555', marginTop:6 }}>
                                    {narrateGroup(hospScores.map(f=>({...f,overall_percentage:f.clause_score})), 'hospitals', clause.code)}
                                </p>
                            </div>
                        )}
                        {hcScores.length > 0 && (
                            <div style={{ marginBottom:16 }}>
                                <div style={{ fontSize:12, fontWeight:700, color:'#5b2d8e', marginBottom:6 }}>
                                    Figure {ci*3+5}: {clause.code} — Health Centres
                                </div>
                                <BarChart items={hcScores.map(f=>({...f, overall_percentage:f.clause_score}))}/>
                                <p style={{ fontSize:11, lineHeight:1.8, color:'#555', marginTop:6 }}>
                                    {narrateGroup(hcScores.map(f=>({...f,overall_percentage:f.clause_score})), 'health centres', clause.code)}
                                </p>
                            </div>
                        )}
                        {clinScores.length > 0 && (
                            <div style={{ marginBottom:16 }}>
                                <div style={{ fontSize:12, fontWeight:700, color:'#c0392b', marginBottom:6 }}>
                                    Figure {ci*3+6}: {clause.code} — Clinics & PHUs
                                </div>
                                <BarChart items={clinScores.map(f=>({...f, overall_percentage:f.clause_score}))}/>
                                <p style={{ fontSize:11, lineHeight:1.8, color:'#555', marginTop:6 }}>
                                    {narrateGroup(clinScores.map(f=>({...f,overall_percentage:f.clause_score})), 'clinics & PHUs', clause.code)}
                                </p>
                            </div>
                        )}

                        {/* ── DISCUSSIONS — QOC docx narratives + ME enrichment ── */}
                        {(() => {
                            // Static discussion texts extracted directly from the QOC AUDITS COMPREHENSIVE REPORT docx
                            // These match exactly the narratives written by the audit team
                            const CLAUSE_DISCUSSIONS = {
                                'AACC': `Overall, 71% of the facilities performed well, achieving an average score above 80%, while 29% scored below 70%. This indicates that most facilities have established processes to conduct initial patient assessments in line with their defined scope of services. Notably, diagnostic services — particularly laboratory and imaging — demonstrated strong availability across most facilities.\n\nHowever, the radiology departments revealed several gaps. All facilities (100%) lacked dosimeters to monitor staff radiation exposure, and although protective aprons were available, they were not consistently used. Additionally, most radiology departments had not clearly defined their scope of services, which may lead to service gaps or inconsistencies in patient care.`,
                                'COP':  `The audit revealed that healthcare facilities generally provide uniform care in accordance with established guidelines and standardized clinical pathways. However, significant gaps were identified in patient transportation and emergency response capacity. Only 17% of the audited facilities have functional patient transportation systems, while the remaining 83% lack adequate ambulance services or have vehicles that are non-operational.\n\nIn terms of emergency care, most facilities do not have documented standard critical pathways for handling emergencies, leading to inconsistent management of critical cases. Within the operating theatre, only 45% of facilities were found to be performing adequately. The main challenges identified include the unavailability of functional anesthetic machines, lack of recovery rooms, and inadequate sterilization capacity. These findings highlight the urgent need for investment in equipment maintenance, infrastructure rehabilitation, and the development of standardized emergency and surgical care protocols to ensure patient safety and quality of care.`,
                                'MOM':  `Management of medication: 75% of the audited health facilities are above 70% and 25% are below 70%. The good practices identified are that safety measures are in place in the pharmacies, there are clear SOPs adopted from National guidelines which are being followed.\n\nMost of the health facilities have good stock management practices with well documented tally cards and medication is labelled clearly. Though some facilities have challenges with space, the pharmacies are kept clean and neat with temperatures being monitored. There is a need for pharmacy personnel(s) to analyze prescriptions and report on adverse events.`,
                                'PRPE': `In patients' rights and patients' education; 98% of the health facilities have patients' rights and responsibilities displayed in both English and SiSwati. The main challenge across the health facilities is documentation of communication and education of the staff and the patients on the patients' rights and responsibilities. Facilities need to develop patient education programs that are culturally appropriate and accessible to all patients, including those with literacy challenges. Patient feedback mechanisms need to be strengthened to ensure patients can report violations of their rights without fear of reprisal.`,
                                'IPCM': `The performance of facilities under the Infection Prevention and Control Management clause varied significantly. While some facilities achieved outstanding results, with one reaching a perfect score of 100%, others performed at an average to poor level. The lowest scoring facility recorded a score of only 18%. Key gaps identified include inadequate hand hygiene infrastructure, inconsistent use of personal protective equipment, poor waste segregation practices, and limited capacity for sterilization of surgical instruments. Strengthening IPC training and ensuring availability of essential supplies remain critical priorities.`,
                                'PSQI': `About 70% of the health facilities have scored above 60% with 3 of the health facilities scoring below 50%. The health facilities have SOPs in place but most of the documents are outdated, which attests to most of them scoring partially met.\n\nThe audit results show that facilities need a clear road map, accountability measures, clear indicators and processes to implement quality management systems. The management involvement is crucial to ensure that processes run smoothly and there is a robust quality improvement approach. Most of the health facilities have quality improvement committees but the functionality of these committees needs to be improved, with regular meetings, documented minutes, and follow-up on agreed action items.`,
                                'GROM': `Governance, roles and responsibilities of management requires sustained attention across the majority of audited facilities. While some facilities demonstrated strong leadership engagement with quality improvement processes, many lack formal governance structures for quality assurance. Facilities need to clearly define reporting lines, establish functional quality committees with documented terms of reference, and ensure that management actively participates in and champions quality improvement initiatives. Regular governance reviews and accountability mechanisms are essential to drive sustained performance improvement.`,
                                'FMS':  `Facility Management, Safety and Support Services presents one of the most significant challenge areas across audited facilities. Key findings include inadequate maintenance of medical equipment, limited emergency preparedness systems including fire safety equipment and evacuation plans, and challenges with waste management infrastructure. Many facilities lack functional biomedical engineering support, leading to prolonged equipment downtime. Investment in preventive maintenance programs, staff safety training, and essential infrastructure upgrading is urgently required to bring facilities to an acceptable standard.`,
                                'HRE':  `Human Resources and Education findings indicate that while most facilities have adequate staffing on paper, there are significant gaps in continuous professional development, staff orientation programs, and performance management systems. Many facilities lack updated staff training records, structured induction programs for new employees, and formal competency assessment processes. Strengthening human resource management practices — including regular performance appraisals, skills gap analysis, and targeted training programs — will be essential to improving overall facility performance.`,
                                'ISM':  `Out of 24 health facilities audited, 3 facilities scored below 60%, while the remaining 21 facilities demonstrated scores above this threshold, with one facility achieving a perfect score of 100%.\n\nInadequate and inconsistent documentation in patient files significantly impacted the facilities' performance. This lack of comprehensive and accurate documentation undermines the effectiveness of the information management system and affects overall compliance. Several facilities struggled due to the absence of structured data collection and reporting tools, inadequate training on health information systems, and limited use of data for decision-making. Facilities need to invest in strengthening their documentation practices, ensuring completeness and accuracy of patient records, and using health data to monitor and improve service delivery.`,
                            };

                            const staticText = CLAUSE_DISCUSSIONS[clause.code];

                            // Build additional ME-based insights to enrich the discussion
                            const ins = getMEInsights(clause.code);
                            let meAddendum = '';
                            if (ins) {
                                if (ins.topNotMet.length > 0 && ins.topNotMet[0].cnt > 1) {
                                    meAddendum += ` A recurring compliance gap observed across multiple facilities was: "${ins.topNotMet[0].txt.trim()}" (noted in ${ins.topNotMet[0].cnt} facilit${ins.topNotMet[0].cnt>1?'ies':'y'}).`;
                                }
                                if (ins.goodPractices.length > 0) {
                                    const gp = (ins.goodPractices[0].area_of_good_practice||'').trim();
                                    if (gp.length > 10) meAddendum += ` Among the areas of good practice observed: ${gp}.`;
                                }
                            }

                            if (!staticText && !meAddendum) return null;

                            return (
                                <div style={{ marginTop:16, background:'#fffbe6', border:'1px solid #ffe58f', borderLeft:'4px solid #faad14', borderRadius:6, padding:'12px 16px' }}>
                                    <div style={{ fontSize:11, fontWeight:700, color:'#7c4d00', marginBottom:8 }}>DISCUSSIONS</div>
                                    {staticText && staticText.split('\n\n').map((para, pi) => (
                                        <p key={pi} style={{ fontSize:11, lineHeight:1.85, color:'#333', marginBottom:pi < staticText.split('\n\n').length-1 ? 10 : 0 }}>{para}</p>
                                    ))}
                                    {meAddendum && (
                                        <p style={{ fontSize:11, lineHeight:1.85, color:'#555', marginTop:staticText ? 10 : 0, fontStyle:'italic' }}>{meAddendum}</p>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                );
            })}

            {/* ── RECOMMENDATIONS ── */}
            <div className="narrative-page" style={{ padding:'28px 40px' }}>
                <div style={{ fontSize:22, fontWeight:900, color:'#1f3864', borderBottom:'3px solid #1f3864', paddingBottom:8, marginBottom:20 }}>5. RECOMMENDATIONS</div>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                    <thead><tr>{['National Level','Regional Level','Facility Level'].map(h=><th key={h} style={hdrStyle}>{h}</th>)}</tr></thead>
                    <tbody>
                        {[
                            ['Development of risk management program','Facilitate sharing of best practices through workshops and conferences','Define scope within facility departments and document processes as SOPs'],
                            ['Strengthen Surveillance of HAIs','Implement regional performance dashboards','Review outdated SOPs and maintain equipment records'],
                            ['Capacitate facilities with fire safety measures','Conduct regular regional audits for compliance','Promote a culture of quality through leadership engagement'],
                            ['Establish a robust Occupational Safety and Health program','Continue Mentorship and technical assistance','Implement effective reporting systems for quality issues'],
                            ['Provision of resources as per facility needs','','Regularly review and address maintenance issues'],
                            ['Involvement of primary healthcare in planning and budgeting','','Apply risk assessment and management uniformly'],
                            ['Proactive system for facility equipment maintenance','',''],
                        ].map((row,i)=>(
                            <tr key={i} style={{ background:i%2?'#f5f5f5':'#fff' }}>
                                {row.map((cell,j)=><td key={j} style={{ ...cellStyle, verticalAlign:'top' }}>{cell}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* ── LIMITATIONS & CONCLUSION ── */}
            <div className="narrative-page" style={{ padding:'28px 40px' }}>
                <div style={{ fontSize:22, fontWeight:900, color:'#1f3864', borderBottom:'3px solid #1f3864', paddingBottom:8, marginBottom:20 }}>6. LIMITATIONS</div>
                <p style={{ fontSize:12, lineHeight:1.9, marginBottom:32 }}>
                    Audit limitations included potential bias due to facility staff awareness of the audit tool and criteria. Varying levels of knowledge and interpretation of quality of care service dimensions by auditors and auditees may have influenced scoring consistency across facilities.
                </p>

                <div style={{ fontSize:22, fontWeight:900, color:'#1f3864', borderBottom:'3px solid #1f3864', paddingBottom:8, marginBottom:20 }}>7. CONCLUSION</div>
                {(() => {
                    const overallAvg = avg(facilities.map(f=>parseFloat(f.overall_percentage||0)));
                    const star1      = ratingCount(1);
                    const star0      = ratingCount(0);
                    const star2plus  = facilities.length - star1 - star0;
                    return (
                        <p style={{ fontSize:12, lineHeight:1.9 }}>
                            The audit of <strong>{facilities.length}</strong> healthcare facilities{filterDesc} during the <strong>{period_label}</strong> period yielded a system-wide average score of <strong>{overallAvg.toFixed(1)}%</strong>.
                            {star2plus > 0 && ` ${star2plus} facilit${star2plus>1?'ies':'y'} achieved 2 stars or higher, demonstrating satisfactory compliance with quality of care standards.`}
                            {` ${star1+star0} facilit${(star1+star0)>1?'ies':'y'} require${(star1+star0)===1?'s':''} focused improvement efforts to meet the minimum quality threshold.`}
                            {` The audited facilities perform well in implementing clinical processes but consistently fall short on documentation, infection control, emergency response capacity, and the operationalisation of quality management systems. Structured corrective action plans, supported by regular mentorship and coaching, will be critical to achieving sustained quality improvement across the health system.`}
                        </p>
                    );
                })()}
            </div>
        </div>
    );
}


// ─── PDF export from the rendered ReportDocument ──────────────────────────────
export function ReportButton({ facility, style }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button type="primary" icon={<FilePdfOutlined/>} onClick={()=>setOpen(true)} style={style} size="small">
                Full Report
            </Button>
            {open && <FacilityReportModal facilityProp={facility} onClose={()=>setOpen(false)}/>}
        </>
    );
}

// ─── Main modal ───────────────────────────────────────────────────────────────
export function FacilityReportModal({ facilityProp, onClose }) {
    const [facilities,     setFacilities]   = useState([]);
    const [selectedFacility, setSelFac]     = useState(facilityProp || null);
    const [assessments,    setAssessments]  = useState([]);
    const [selectedAsmtId, setSelAsmt]      = useState(null);
    const [loading,        setLoading]      = useState(false);
    const [report,         setReport]       = useState(null);
    const [pdfProgress,    setPdfProgress]  = useState(0);
    const [exporting,      setExporting]    = useState(false);
    const [xlsExporting,   setXlsExporting] = useState(false);
    const [narrativeVisible, setNarrativeVisible] = useState(false);
    const narrativeRef = useRef(null);
    const [reportMode,       setReportMode]       = useState('facility');
    const [consolidatedData, setConsolidatedData] = useState(null);
    const [consolidating,    setConsolidating]    = useState(false);
    const [conPeriod,        setConPeriod]        = useState('');
    const [periods,          setPeriods]          = useState([]);
    const [conFilterType,    setConFilterType]    = useState('all');
    const [conFilterRegion,  setConFilterRegion]  = useState('all');
    const [availRegions,     setAvailRegions]     = useState([]);
    const [availTypes,       setAvailTypes]       = useState([]);
    const [meLoading,        setMeLoading]        = useState(false); // lazy ME enrichment

    useEffect(() => {
        if (!facilityProp) {
            FacilityService.all({ status:1 }).then(r => setFacilities(r.data?.result?.data||[]));
        }
        QoCService.getPeriods().then(r => {
            const ps = r.data?.data || [];
            setPeriods(ps);
            const cur = ps.find(p => p.is_current == 1);
            if (cur) setConPeriod(cur.label);
        }).catch(()=>{});
    }, []);

    useEffect(() => {
        if (selectedFacility?.id) {
            QoCService.getAssessments({ facility_id: selectedFacility.id, status:'approved' })
                .then(r => {
                    const list = r.data?.data || [];
                    const byPeriod = {};
                    list.forEach(a => {
                        const key = a.period_label?.trim() || a.assessment_date?.slice(0, 7);
                        if (!key) return;
                        if (!byPeriod[key]) byPeriod[key] = [];
                        byPeriod[key].push(a);
                    });
                    const grouped = Object.entries(byPeriod)
                        .sort(([a],[b]) => b.localeCompare(a))
                        .map(([period, items]) => {
                            const dates = items.map(a => a.assessment_date).filter(Boolean).sort();
                            const latestDate = dates[dates.length - 1] || '';
                            const scores = items.map(a => parseFloat(a.overall_percentage || 0)).filter(s => s > 0);
                            const avg = scores.length ? scores.reduce((s, v) => s + v, 0) / scores.length : 0;
                            return {
                                id: items[0].id,
                                siblingIds: items.map(a => a.id),
                                period_label: period,
                                assessment_date: latestDate,
                                overall_percentage: avg.toFixed(1),
                                clause_count: items.length,
                            };
                        });
                    setAssessments(grouped);
                    if (grouped.length) setSelAsmt(grouped[0].id);
                });
        }
    }, [selectedFacility]);

    useEffect(() => { if (selectedAsmtId) loadReport(selectedAsmtId); }, [selectedAsmtId]); // eslint-disable-line

    const loadReport = async (asmtId) => {
        setLoading(true); setReport(null);
        try {
            const grouped = assessments.find(a => a.id === asmtId);
            const allIds  = grouped?.siblingIds?.length ? grouped.siblingIds : [asmtId];
            const primary = await QoCService.getAssessmentDetail(asmtId);
            const assessment = primary.data?.data || {};
            const results = await Promise.all(
                allIds.map(id => Promise.all([
                    QoCService.getClauseScores(id),
                    QoCService.getAssessmentResponsesME(id),
                ]))
            );
            const clauseScoresAll = results.flatMap(([s]) => s.data?.data || []);
            const clauseMap = {};
            clauseScoresAll.forEach(s => {
                if (s.clause_code && (!clauseMap[s.clause_code] || parseFloat(s.total_score||0) > 0)) {
                    clauseMap[s.clause_code] = s;
                }
            });
            const clauseScores = Object.values(clauseMap);
            const responses = results.flatMap(([,r]) => r.data?.data || []);
            const assessed = clauseScores.filter(s => parseFloat(s.total_score||0) > 0);
            const overallPct = assessed.length
                ? assessed.reduce((sum, s) => sum + parseFloat(s.total_score||0), 0) / assessed.length
                : parseFloat(assessment.overall_percentage || 0);
            assessment.overall_percentage = overallPct.toFixed(2);
            setReport({ assessment, clauseScores, responses });
        } catch(e) { message.error('Failed to load report data: ' + e.message); console.error(e); }
        finally  { setLoading(false); }
    };

    // ── Load consolidated data — FAST PATH (scores only, no ME blocking) ────────
    const loadConsolidated = async () => {
        if (!conPeriod) { message.warning('Please select a period'); return; }
        setConsolidating(true);
        setConsolidatedData(null);
        try {
            // 1. Assessments list (lightweight — just scores + clause codes)
            const aRes = await QoCService.getAssessments({ period_label: conPeriod, status: 'approved' });
            const allAssessments = aRes.data?.data || [];

            // 2. Facility profiles (one call)
            const facRes = await FacilityService.all({ status: 1 });
            const allFacs = facRes.data?.result?.data || [];
            const facMap  = Object.fromEntries(allFacs.map(f => [f.id, f]));

            // 3. Build per-facility score map (pure in-memory, no extra API calls)
            const facIds = [...new Set(allAssessments.map(a => a.facility_id))];
            const facilitiesWithScores = facIds.map(fid => {
                const fac      = facMap[fid] || { id: fid, business_name: 'Unknown', site_type: '' };
                const facAsmts = allAssessments.filter(a => a.facility_id === fid);
                const clauseScores = {};
                facAsmts.forEach(a => {
                    if (a.clause_code && a.overall_percentage)
                        clauseScores[a.clause_code] = parseFloat(a.overall_percentage);
                });
                const scores  = Object.values(clauseScores).filter(Boolean);
                const overall = scores.length > 0 ? scores.reduce((s,v)=>s+v,0)/scores.length : 0;
                return { ...fac, clause_scores: clauseScores, overall_percentage: overall.toFixed(2) };
            });

            // 4. Clause summaries (in-memory)
            const clauseSummaries = CLAUSES.map(clause => {
                const scores = facilitiesWithScores
                    .map(f => parseFloat(f.clause_scores?.[clause.code] || 0)).filter(s => s > 0);
                const avg = scores.length > 0 ? scores.reduce((a,b)=>a+b,0)/scores.length : 0;
                return { clause_code:clause.code, avg_score:avg,
                    above_70:scores.filter(s=>s>=70).length, below_70:scores.filter(s=>s<70).length };
            });

            // 5. Audit teams (one call, non-blocking if it fails)
            let auditTeams = [];
            try {
                const teamsRes = await QoCService.getAuditTeams({ period_label: conPeriod });
                const teams = teamsRes.data?.data || [];
                const seen = new Set();
                teams.forEach(t => {
                    const members = Array.isArray(t.members) ? t.members :
                        (typeof t.members === 'string' ? JSON.parse(t.members||'[]') : []);
                    members.forEach(m => {
                        const key = m.user_name||m.name||'';
                        if (key && !seen.has(key)) { seen.add(key); auditTeams.push({...m, region:t.facility_name}); }
                    });
                });
            } catch(e) {}

            const types   = [...new Set(facilitiesWithScores.map(f=>f.site_type).filter(Boolean))].sort();
            const regions = [...new Set(facilitiesWithScores.map(f=>f.located_region||f.region).filter(Boolean))].sort();
            setAvailTypes(types);
            setAvailRegions(regions);
            setConFilterType('all');
            setConFilterRegion('all');

            // ── Show the report immediately with empty meResponses ────────────────
            const baseData = {
                period_label:     conPeriod,
                facilities:       facilitiesWithScores,
                clause_summaries: clauseSummaries,
                audit_teams:      auditTeams,
                meResponses:      {},   // starts empty — enriched in background
                _allAssessments:  allAssessments,
                _facMap:          facMap,
                overall_stats: {
                    total:     facilitiesWithScores.length,
                    avg_score: facilitiesWithScores.reduce((s,f)=>s+parseFloat(f.overall_percentage||0),0)/(facilitiesWithScores.length||1),
                },
            };
            setConsolidatedData(baseData);

            trailReportGenerate(TRAIL_MODULES.FACILITY_REPORT, 'Consolidated Report',
                { period: conPeriod, facilityCount: facilitiesWithScores.length,
                  filterType: conFilterType, filterRegion: conFilterRegion });
            message.success(`Report loaded — ${facilitiesWithScores.length} facilities`);

            // ── BACKGROUND: lazily enrich with ME responses (doesn't block UI) ──
            setMeLoading(true);
            setTimeout(async () => {
                try {
                    const allAsmtIds = allAssessments.map(a => a.id);
                    const BATCH = 15;
                    const meResponses = {};
                    for (let i = 0; i < allAsmtIds.length; i += BATCH) {
                        const batch = allAsmtIds.slice(i, i + BATCH);
                        const batchResults = await Promise.all(
                            batch.map(id => QoCService.getAssessmentResponsesME(id)
                                .then(r => {
                                    const asmt = allAssessments.find(a => a.id === id);
                                    const fac  = facMap[asmt?.facility_id] || {};
                                    return (r.data?.data || []).map(me => ({
                                        ...me,
                                        business_name: fac.business_name||'',
                                        site_type:     fac.site_type||'',
                                    }));
                                })
                                .catch(() => [])
                            )
                        );
                        batchResults.flat().forEach(me => {
                            if (!me.clause_code) return;
                            if (!meResponses[me.clause_code]) meResponses[me.clause_code] = [];
                            meResponses[me.clause_code].push(me);
                        });
                    }
                    // Merge into existing consolidated data (React re-render updates narratives)
                    setConsolidatedData(prev => prev ? { ...prev, meResponses } : prev);
                    message.success('Narrative enrichment complete');
                } catch(e) {
                    console.warn('ME enrichment failed (non-fatal):', e.message);
                } finally {
                    setMeLoading(false);
                }
            }, 100); // slight delay so UI renders first

        } catch(e) {
            message.error('Failed to load consolidated data: ' + e.message);
            setConsolidating(false);
            setMeLoading(false);
        } finally {
            setConsolidating(false);
        }
    };

    // ── Browser print — preserves colours ────────────────────────────────────
        const handlePrint = () => {
        trailReportGenerate(TRAIL_MODULES.FACILITY_REPORT, 'Facility Audit PDF',
            { facilityName: selectedFacility?.business_name, period: report?.assessment?.assessment_date });
        const content = document.getElementById('qoc-full-report');
        if (!content) return;
        const w = window.open('', '_blank');
        w.document.write(`<!DOCTYPE html>
<html><head>
<title>QoC Audit Report — ${selectedFacility?.business_name || ''}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  html{font-size:10px}
  body{
    font-family:'Segoe UI',Arial,sans-serif;
    font-size:10px;
    line-height:1.5;
    color:#222;
    background:#fff;
    -webkit-print-color-adjust:exact;
    print-color-adjust:exact;
    color-adjust:exact;
  }
  .report-page{
    display:block;
    width:100%;
    padding:10mm 12mm;
    break-before:page;
    page-break-before:always;
  }
  .report-page:first-child{
    break-before:avoid;
    page-break-before:avoid;
  }
  table{border-collapse:collapse;width:100%}
  th,td{font-size:9.5px!important}
  [style*="fontSize:12"]{font-size:10px!important}
  [style*="fontSize:11"]{font-size:9.5px!important}
  [style*="fontSize:10"]{font-size:9px!important}
  img{max-width:100%;height:auto}
  @page{size:A4 portrait;margin:8mm}
  @media print{
    *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}
    .report-page{break-before:page;page-break-before:always}
    .report-page:first-child{break-before:avoid;page-break-before:avoid}
  }
</style>
</head><body>${content.innerHTML}</body></html>`);
        w.document.close();
        setTimeout(() => { w.focus(); w.print(); }, 800);
    };

    // ── Consolidated print — preserves colours ────────────────────────────────

    const handleConsolidatedPrint = () => {
        trailReportExport(TRAIL_MODULES.FACILITY_REPORT, 'Consolidated Report', 'PDF',
            { period: conPeriod, facilityCount: consolidatedData?.facilities?.length });
        const el = document.getElementById('consolidated-report');
        if (!el) return;
        const win = window.open('', '_blank');
        win.document.write(`<!DOCTYPE html>
<html><head>
<title>QoC Consolidated Report — ${conPeriod}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  html{font-size:10px}
  body{
    font-family:'Segoe UI',Arial,sans-serif;
    font-size:10px;
    line-height:1.55;
    color:#222;
    background:#fff;
    -webkit-print-color-adjust:exact;
    print-color-adjust:exact;
    color-adjust:exact;
  }
  /* Sections flow naturally — break BEFORE each new section.
     This prevents the double-page-break / blank page caused by
     page-break-after on an element whose content already pushed
     the browser to a new page. */
  .narrative-page{
    display:block;
    width:100%;
    padding:12mm 14mm;
    break-before:page;
    page-break-before:always;
  }
  .narrative-page:first-child{
    break-before:avoid;
    page-break-before:avoid;
  }
  /* Let long content flow across physical pages naturally */
  .narrative-page p,
  .narrative-page div,
  .narrative-page table{
    break-inside:auto;
    page-break-inside:auto;
  }
  /* Override all inline font sizes to be print-appropriate */
  p{font-size:10px!important;line-height:1.55!important;margin-bottom:6px!important}
  table{border-collapse:collapse;width:100%;font-size:9.5px!important}
  th,td{font-size:9.5px!important;padding:4px 7px!important}
  /* Section headings */
  [style*="fontSize:22"]{font-size:16px!important}
  [style*="fontSize:17"],[style*="fontSize:16"]{font-size:13px!important}
  [style*="fontSize:15"],[style*="fontSize:14"],[style*="fontSize:13"]{font-size:11px!important}
  [style*="fontSize:12"]{font-size:10px!important}
  [style*="fontSize:11"]{font-size:9.5px!important}
  [style*="fontSize:10"]{font-size:9px!important}
  /* Bar chart rows */
  [style*="height:20"]{height:14px!important}
  /* Remove minHeight so pages don't force blank space */
  [style*="minHeight"]{min-height:0!important}
  /* Tighten gap spacing */
  [style*="marginBottom:20"]{margin-bottom:8px!important}
  [style*="marginBottom:16"]{margin-bottom:6px!important}
  [style*="marginBottom:14"]{margin-bottom:5px!important}
  [style*="marginBottom:12"]{margin-bottom:5px!important}
  [style*="gap:8"]{gap:4px!important}
  img{max-width:100%;height:auto}
  @page{
    size:A4 portrait;
    margin:8mm;
  }
  @media print{
    *{
      -webkit-print-color-adjust:exact!important;
      print-color-adjust:exact!important;
      color-adjust:exact!important;
    }
    .narrative-page{
      break-before:page;
      page-break-before:always;
    }
    .narrative-page:first-child{
      break-before:avoid;
      page-break-before:avoid;
    }
  }
</style>
</head><body>${el.outerHTML}</body></html>`);
        win.document.close();
        setTimeout(() => win.print(), 900);
    };

    // ── Narrative PDF ─────────────────────────────────────────────────────────
    const handleNarrativePdf = async () => {
        if (!report || exporting) return;
        setExporting(true);
        setPdfProgress(0);
        try {
            setNarrativeVisible(true);
            await new Promise(resolve => setTimeout(resolve, 800));
            setPdfProgress(15);

            const [jspdfMod, h2cMod] = await Promise.all([
                import('jspdf'),
                import('html2canvas'),
            ]);
            const jsPDFLib       = jspdfMod.jsPDF || jspdfMod.default?.jsPDF || jspdfMod.default;
            const html2canvasLib = h2cMod.default || h2cMod;
            setPdfProgress(22);

            const el = narrativeRef.current?.querySelector('#narrative-report')
                     || document.getElementById('narrative-report');
            if (!el) throw new Error('Narrative report element not found');

            const canvas = await html2canvasLib(el, {
                scale: 1.8,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#e2e8f0',
                scrollY: 0, scrollX: 0,
                windowWidth:  el.scrollWidth,
                windowHeight: el.scrollHeight,
                logging: false,
            });

            setNarrativeVisible(false);
            setPdfProgress(68);

            const PAGE_W   = 210;
            const PAGE_H   = 297;
            const MG       = 10;
            const HEADER_H = 12;
            const FOOTER_H = 8;
            const usableW  = PAGE_W - MG * 2;
            const usableH  = PAGE_H - HEADER_H - FOOTER_H - MG;
            const scale    = canvas.width > 0 ? usableW / canvas.width : 1;

            const pageEls = el.querySelectorAll('.narrative-page');
            const elRect  = el.getBoundingClientRect();
            const sectionStartsPx = Array.from(pageEls).map(pg => {
                const r = pg.getBoundingClientRect();
                return Math.round((r.top - elRect.top) * 1.8);
            }).filter(y => y >= 0).sort((a,b) => a - b);

            const pages = [];
            for (let i = 0; i < sectionStartsPx.length; i++) {
                const start = sectionStartsPx[i];
                const end   = i + 1 < sectionStartsPx.length ? sectionStartsPx[i + 1] : canvas.height;
                const pageHpx = usableH / scale;
                if (end - start <= pageHpx * 1.05) {
                    pages.push({ start, end });
                } else {
                    let cur = start;
                    while (cur < end) {
                        const next = Math.min(cur + pageHpx, end);
                        pages.push({ start: cur, end: next });
                        cur = next;
                    }
                }
            }

            const totalPages  = pages.length;
            const pdf         = new jsPDFLib({ orientation:'portrait', unit:'mm', format:'a4' });
            const fname_safe  = (selectedFacility?.business_name||'Facility').replace(/\s+/g,'_');
            const generatedAt = new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' });
            const period      = report.assessment?.assessment_date || '';

            const drawChrome = (pn) => {
                pdf.setFillColor(15,23,42);
                pdf.rect(0,0,PAGE_W,HEADER_H,'F');
                pdf.setTextColor(255,255,255);
                pdf.setFontSize(7.5); pdf.setFont('helvetica','bold');
                pdf.text('ESWATINI MINISTRY OF HEALTH — National Quality Management Programme', MG, 8);
                pdf.setFont('helvetica','normal'); pdf.setFontSize(7);
                pdf.text(`QoC Audit Report  |  ${selectedFacility?.business_name||''}  |  Page ${pn} of ${totalPages}`, PAGE_W-MG, 8, { align:'right' });
                pdf.setFillColor(241,245,249);
                pdf.rect(0, PAGE_H-FOOTER_H, PAGE_W, FOOTER_H, 'F');
                pdf.setTextColor(100,116,139); pdf.setFontSize(6.5);
                pdf.text('Confidential — Eswatini Ministry of Health QoC Platform', MG, PAGE_H-3);
                pdf.text(`Generated: ${generatedAt}  |  ${pn}/${totalPages}`, PAGE_W-MG, PAGE_H-3, { align:'right' });
            };

            for (let i = 0; i < pages.length; i++) {
                if (i > 0) pdf.addPage('a4','portrait');
                drawChrome(i+1);
                const { start, end } = pages[i];
                const sliceH = end - start;
                const dstH   = sliceH * scale;
                const sc = document.createElement('canvas');
                sc.width  = canvas.width;
                sc.height = sliceH;
                sc.getContext('2d').drawImage(canvas, 0, start, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
                const safeDstH = Math.max(1, Math.round(dstH * 100) / 100);
                if (safeDstH > 0 && sc.height > 0 && canvas.width > 0) {
                    pdf.addImage(sc.toDataURL('image/jpeg', 0.9), 'JPEG', MG, HEADER_H+1, usableW, safeDstH);
                }
                setPdfProgress(68 + Math.round(((i+1)/pages.length)*28));
            }

            pdf.save(`QoC_Narrative_Report_${fname_safe}_${period}.pdf`);
            setPdfProgress(100);
            trailReportExport(TRAIL_MODULES.FACILITY_REPORT, 'Narrative PDF Report', 'PDF',
                { facilityName: selectedFacility?.business_name, pages: totalPages });
            message.success(`Narrative PDF downloaded — ${totalPages} pages`);
        } catch(err) {
            message.error('Narrative PDF failed: ' + err.message);
            console.error(err);
        } finally {
            setTimeout(() => { setExporting(false); setPdfProgress(0); }, 1200);
        }
    };

    const handleExcelExport = async () => {
        if (!report) return;
        setXlsExporting(true);
        try {
            await exportFacilityAssessmentExcel({
                facility:     selectedFacility,
                assessment:   report.assessment,
                clauseScores: report.clauseScores,
                responses:    report.responses,
            });
           
            trailReportExport(TRAIL_MODULES.FACILITY_REPORT, 'Facility Assessment', 'Excel',
                { facilityName: selectedFacility?.business_name });
            message.success('Excel file downloaded');
        } catch(e) { message.error('Export failed: ' + e.message); }
        finally    { setXlsExporting(false); }
    };

    const assessment = report?.assessment || assessments.find(a => a.id === selectedAsmtId);

    return (
        <Modal open onCancel={onClose} footer={null} width={1100} style={{ top:10 }}
            bodyStyle={{ padding:0 }}
            title={
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <FilePdfOutlined style={{ color:'#f5222d', fontSize:20 }}/>
                    <span style={{ fontWeight:800 }}>Facility Audit Report</span>
                </div>
            }
            destroyOnClose>

            {/* Report mode toggle */}
            <div style={{ padding:'10px 20px', borderBottom:'1px solid #f0f0f0', background:'#f0f5ff',
                display:'flex', alignItems:'center', gap:16 }}>
                <span style={{ fontWeight:600, fontSize:12 }}>Report Type:</span>
                <Radio.Group value={reportMode} onChange={e => setReportMode(e.target.value)} size="small">
                    <Radio.Button value="facility">🏥 Facility Report</Radio.Button>
                    <Radio.Button value="consolidated">📊 Consolidated Report</Radio.Button>
                </Radio.Group>
            </div>

            {/* Consolidated controls */}
            {reportMode === 'consolidated' && (
                <div style={{ padding:'12px 20px', borderBottom:'1px solid #f0f0f0', background:'#fafbff',
                    display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                    <Select placeholder="Select Period" style={{ minWidth:220 }}
                        value={conPeriod || undefined}
                        onChange={v => { setConPeriod(v); setConsolidatedData(null); }}>
                        {periods.map(p => (
                            <Select.Option key={p.label} value={p.label}>
                                {p.is_current==1 ? '★ ' : ''}{p.label}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button type="primary" loading={consolidating} onClick={loadConsolidated}
                        style={{ background:'#1f3864', borderColor:'#1f3864' }}>
                        Load Consolidated Report
                    </Button>
                    {consolidatedData && availTypes.length > 0 && (
                        <Select value={conFilterType} onChange={setConFilterType} style={{ minWidth:160 }}>
                            <Select.Option value="all">All Facility Types</Select.Option>
                            {availTypes.map(t => <Select.Option key={t} value={t}>{t}</Select.Option>)}
                        </Select>
                    )}
                    {consolidatedData && availRegions.length > 1 && (
                        <Select value={conFilterRegion} onChange={setConFilterRegion} style={{ minWidth:160 }}>
                            <Select.Option value="all">All Regions</Select.Option>
                            {availRegions.map(r => <Select.Option key={r} value={r}>{r} Region</Select.Option>)}
                        </Select>
                    )}
                    {consolidatedData && (
                        <>
                            <Tag color="green">{consolidatedData.facilities.length} facilities loaded</Tag>
                            {meLoading && (
                                <Tag color="processing" icon={<LoadingOutlined/>}>
                                    Enriching narratives…
                                </Tag>
                            )}
                            <Button icon={<PrinterOutlined/>} onClick={handleConsolidatedPrint}>
                                Print / Save PDF
                            </Button>
                        </>
                    )}
                </div>
            )}

            {/* Facility controls */}
            <div style={{ padding:'12px 20px', borderBottom:'1px solid #f0f0f0', background:'#fafbff',
                display: reportMode === 'facility' ? 'flex' : 'none', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                {!facilityProp && (
                    <Select showSearch placeholder="Select Facility" style={{ minWidth:260 }}
                        optionFilterProp="children" value={selectedFacility?.id}
                        onChange={id => setSelFac(facilities.find(f=>f.id===id))}>
                        {facilities.map(f=><Option key={f.id} value={f.id}>{f.business_name}</Option>)}
                    </Select>
                )}
                {selectedFacility && (
                    <Select placeholder="Select Assessment" style={{ minWidth:280 }}
                        value={selectedAsmtId} onChange={setSelAsmt}
                        notFoundContent="No approved assessments">
                        {assessments.map(a=>(
                            <Option key={a.id} value={a.id}>
                                {a.period_label || moment(a.assessment_date).format('MMM YYYY')}
                                {a.overall_percentage ? ` — ${parseFloat(a.overall_percentage).toFixed(1)}%` : ''}
                                {a.assessment_date ? ` · ${moment(a.assessment_date).format('DD MMM YY')}` : ''}
                                {` (${a.clause_count} clause${a.clause_count !== 1 ? 's' : ''})`}
                            </Option>
                        ))}
                    </Select>
                )}

                <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
                    <Button icon={<PrinterOutlined/>} onClick={handlePrint} disabled={!report}>
                        Print / Save PDF
                    </Button>
                    <Button icon={<FileExcelOutlined/>} onClick={handleExcelExport}
                        disabled={!report} loading={xlsExporting}
                        style={{ borderColor:'#52c41a', color:'#52c41a' }}>
                        Download Excel
                    </Button>
                    <Button
                        icon={exporting ? <LoadingOutlined/> : <FilePdfOutlined/>}
                        onClick={handleNarrativePdf}
                        disabled={!report || exporting}
                        style={{ borderColor:'#f5222d', color:'#f5222d', minWidth:160, fontWeight:600 }}>
                        {exporting ? `Generating… ${pdfProgress}%` : 'Narrative PDF Report'}
                    </Button>
                </div>
            </div>

            {/* Progress bar */}
            {exporting && (
                <div style={{ padding:'6px 20px', background:'#fff7e6', borderBottom:'1px solid #ffe58f' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <span style={{ fontSize:12, color:'#614700' }}>📄 Capturing report as PDF…</span>
                        <div style={{ flex:1 }}>
                            <div style={{ background:'#ffe58f', borderRadius:4, height:6, overflow:'hidden' }}>
                                <div style={{ background:'#fa8c16', height:'100%', width:`${pdfProgress}%`, transition:'width 0.3s', borderRadius:4 }}/>
                            </div>
                        </div>
                        <span style={{ fontSize:12, fontWeight:700, color:'#fa8c16' }}>{pdfProgress}%</span>
                    </div>
                </div>
            )}

            {/* Facility quick info */}
            {selectedFacility && (
                <div style={{ padding:'8px 20px', background:'#f0f7ff', borderBottom:'1px solid #dbeafe',
                    fontSize:12, display:'flex', gap:16, flexWrap:'wrap', color:'#555' }}>
                    <span><strong>Facility:</strong> {selectedFacility.business_name}</span>
                    <span><strong>Level:</strong> {selectedFacility.site_type||'N/A'}</span>
                    <span><strong>Region:</strong> {selectedFacility.located_region||'N/A'}</span>
                    <span><strong>Owner:</strong> {selectedFacility.facility_owner||'N/A'}</span>
                    {assessment && <span><strong>Audit Date:</strong> {moment(assessment.assessment_date).format('DD MMM YYYY')}</span>}
                </div>
            )}

            {/* Report preview */}
            <div style={{ maxHeight:'75vh', overflowY:'auto', padding:'0 0 20px' }}>

                {/* Consolidated report view */}
                {reportMode === 'consolidated' && !consolidatedData && !consolidating && (
                    <div style={{ padding:60, textAlign:'center', color:'#aaa' }}>
                        <div style={{ fontSize:48, marginBottom:12 }}>📊</div>
                        <div style={{ fontWeight:600 }}>Select a period and click "Load Consolidated Report"</div>
                        <div style={{ fontSize:12, marginTop:6 }}>Pulls all approved assessments across all facilities for the selected period</div>
                    </div>
                )}
                {reportMode === 'consolidated' && consolidating && (
                    <div style={{ padding:60, textAlign:'center' }}>
                        <Spin size="large" tip="Loading data from all facilities..."/>
                    </div>
                )}
                {reportMode === 'consolidated' && consolidatedData && !consolidating && (() => {
                    const filtered = {
                        ...consolidatedData,
                        facilities: consolidatedData.facilities.filter(f => {
                            const typeMatch   = conFilterType   === 'all' || f.site_type === conFilterType;
                            const regionMatch = conFilterRegion === 'all' || (f.located_region||f.region) === conFilterRegion;
                            return typeMatch && regionMatch;
                        }),
                        filterType:   conFilterType,
                        filterRegion: conFilterRegion,
                    };
                    return <ConsolidatedReportDocument data={filtered}/>;
                })()}

                {/* Facility report view */}
                {reportMode === 'facility' && (!selectedFacility ? (
                    <div style={{ padding:60, textAlign:'center', color:'#aaa' }}>
                        <FilePdfOutlined style={{ fontSize:48, display:'block', marginBottom:12 }}/>
                        Select a facility to generate the report
                    </div>
                ) : loading ? (
                    <div style={{ padding:60, textAlign:'center' }}><Spin size="large" tip="Loading report data..."/></div>
                ) : assessments.length === 0 ? (
                    <div style={{ padding:40 }}><Alert message="No approved assessments found for this facility." type="info" showIcon/></div>
                ) : !report ? (
                    <div style={{ padding:60, textAlign:'center' }}><Spin size="large" tip="Building report..."/></div>
                ) : (
                    <div style={{ padding:'0 20px' }}>
                        <div style={{ background:'#fff8e1', border:'1px solid #ffe58f', borderRadius:6,
                            padding:'8px 14px', marginBottom:12, fontSize:12, color:'#614700' }}>
                            💡 <strong>Two export options:</strong>
                            &nbsp;<strong>Print / Save PDF</strong> = browser print dialog (fast, full colour).
                            &nbsp;<strong>Narrative PDF Report</strong> = pixel-perfect PDF capturing exactly what you see here.
                        </div>
                        <ReportDocument
                            facility={selectedFacility}
                            assessment={report.assessment}
                            clauseScores={report.clauseScores}
                            responses={report.responses}
                        />
                    </div>
                ))}
            </div>

            {/* Hidden narrative doc for html2canvas capture */}
            {narrativeVisible && report && (
                <div ref={narrativeRef} style={{
                    position:'fixed', left:'-9999px', top:0,
                    width:794, background:'#fff', zIndex:-999,
                    overflow:'visible', pointerEvents:'none',
                }}>
                    <NarrativeReportDocument
                        facility={selectedFacility}
                        assessment={report.assessment}
                        clauseScores={report.clauseScores}
                        responses={report.responses}
                        capItems={report.capItems||[]}
                        auditTeam={report.auditTeam||null}
                    />
                </div>
            )}
        </Modal>
    );
}

export default function QoCFacilityReport() {
    return <FacilityReportModal onClose={() => window.history.back()} />;
}